import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PageDataLoader } from '../../../components/BlueXLoader';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { queryClient } from '../../../queryClient';
import {
  manageUsersListQueryKey,
  manageUsersOrgsQueryKey,
  manageUsersSitesQueryKey,
} from '../../../queryCacheScope';
import './profile.css';
import '../../scans/scans.css';
import { getProfile, updateProfile, listUsersAdmin, updateUserAdmin, resendUserInvitation, deleteUserAdmin, listOrganizations } from '../../../services/user.service';
import { getSites } from '../../../services/asset.service';
import { sendMfaOtpChallenge, verifyMfaOtpChallenge, getMfaStatus, disableMfaMethod, getTotpSetup, activateTotpCode, getMfaRecoveryCodes, generateMfaRecoveryCodes, requestPasswordReset } from '../../../services/auth.service';
import {
  compareLocaleStrings,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  MANAGE_USERS_TABLE_COLUMNS,
  MANAGE_USERS_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import { TrashIcon } from '../../../shared/components/TrashIcon';
import MfaOtpCodeSlots, { MFA_OTP_SLOT_COUNT } from '../../../shared/components/MfaOtpCodeSlots';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import { pageToPath } from '../../../routes/dashboardRoutes';
import '../../assets/assets.css';
import '../../vulnerabilities/vulnerabilities.css';
import '../users-orgs/users-orgs.css';

/** Match Assets / Scans table sort affordance (⇅ / ↑ / ↓). */
function getScansStyleSortIcon(sortConfig, key) {
  if (sortConfig?.key !== key) return '⇅';
  return sortConfig.direction === 'asc' ? '↑' : '↓';
}

const MANAGE_USERS_CACHE_MS = 3 * 60 * 1000;
const manageUsersCacheKey = (orgId) => manageUsersListQueryKey(orgId);
const manageUsersOrgsCacheKey = () => manageUsersOrgsQueryKey();
const manageUsersSitesCacheKey = () => manageUsersSitesQueryKey();

function manageUsersCacheIsFresh(key) {
  const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt || 0;
  return updatedAt > 0 && Date.now() - updatedAt < MANAGE_USERS_CACHE_MS;
}

const PROFILE_TAB_ICONS = {
  general: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5.5 19.25c1.4-3.1 3.7-4.6 6.5-4.6s5.1 1.5 6.5 4.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ),
  security: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 3.75 18.5 6.2v5.1c0 4.05-2.7 7.55-6.5 8.95-3.8-1.4-6.5-4.9-6.5-8.95V6.2L12 3.75Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M9.75 12.1 11.3 13.6 14.4 10.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};
const MANAGE_USERS_ROLE_OPTIONS = ['Admin', 'Analyst', 'Viewer', 'Partner'];
const UNIVERSAL_SEARCH_MAX_LEN = 500;

function sanitizeUniversalSearchInput(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, UNIVERSAL_SEARCH_MAX_LEN);
}

function getPreferredTimezoneDefault() {
  return 'Australia/Melbourne';
}

function getTimezoneOptions(currentTz) {
  const fallback = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney'
  ];
  let values = fallback;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const tzs = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(tzs) && tzs.length > 0) values = tzs;
    }
  } catch (_) {
    // Keep fallback list when browser cannot provide full timezone list.
  }
  const set = new Set(values);
  if (currentTz) set.add(currentTz);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function normalizeUiRole(profile) {
  if (profile?.is_superuser === true) return 'superuser';
  const rawRole = profile?.role;
  const roleStr = String(
    typeof rawRole === 'string'
      ? rawRole
      : (rawRole?.name ?? rawRole?.label ?? rawRole?.role ?? rawRole ?? '')
  ).trim().toLowerCase();
  if (!roleStr) return 'viewer';
  if (roleStr === 'superuser' || roleStr === 'root') return 'superuser';
  if (roleStr === 'partner' || roleStr === '4') return 'partner';
  if (roleStr === 'admin' || roleStr === 'administrator' || roleStr === '1') return 'admin';
  if (roleStr === 'analyst' || roleStr === '2') return 'analyst';
  if (roleStr === 'viewer' || roleStr === '3') return 'viewer';
  return roleStr;
}

const EMPTY_USER_PROFILE = {
  name: '',
  email: '',
  organizationName: '',
  role: '',
  timezone: getPreferredTimezoneDefault(),
  is_superuser: false,
  capabilities: null,
};

function mapApiProfileToUserProfile(data, prev = EMPTY_USER_PROFILE) {
  if (!data || typeof data !== 'object') return prev;
  const name = String(data?.name ?? data?.display_name ?? '').trim();
  const email = String(data?.email ?? '').trim();
  const organizationName = String(
    data?.organization_name ??
    data?.org_name ??
    data?.organization?.name ??
    data?.company_name ??
    ''
  ).trim();
  const roleRaw = data?.role;
  const roleValue = String(
    typeof roleRaw === 'string'
      ? roleRaw
      : (roleRaw?.name ?? roleRaw?.label ?? roleRaw?.role ?? roleRaw ?? '')
  ).trim();
  const roleLookup = { '1': 'Admin', '2': 'Analyst', '3': 'Viewer', '4': 'Partner' };
  const role = roleLookup[roleValue] || roleValue || '';
  const timezone = String(data?.timezone || '').trim() || getPreferredTimezoneDefault();
  if (!name && !email && !organizationName && !role && data?.is_superuser !== true) return prev;
  return {
    ...prev,
    name: name || prev.name || '',
    email: email || prev.email || '',
    organizationName: organizationName || prev.organizationName || '',
    role: role || prev.role || '',
    timezone,
    is_superuser: data?.is_superuser === true,
    capabilities: data?.capabilities && typeof data.capabilities === 'object'
      ? data.capabilities
      : (prev.capabilities || null),
  };
}

function buildUiAccessPolicy(profile) {
  const role = normalizeUiRole(profile);
  const isSuperuser = role === 'superuser';
  const isPartner = role === 'partner';
  const effectiveCaps = profile?.capabilities?.effective_capabilities || {};
  const profileCaps = profile?.capabilities || {};
  const hasCapability = (key) =>
    profileCaps?.[key] === true || effectiveCaps?.[key] === true;
  const canManageUsers =
    isSuperuser ||
    isPartner ||
    effectiveCaps.can_manage_users === true ||
    role === 'admin';
  const canManageOrganizations =
    isSuperuser ||
    isPartner ||
    role === 'admin' ||
    effectiveCaps.can_manage_organizations === true;
  const canSeeSettingsConnectors = isSuperuser || isPartner || hasCapability('can_access_connectors');
  const canManageSettingsConnectors = isSuperuser || isPartner || hasCapability('can_manage_connectors');
  const canSeeSettingsDeployment = isSuperuser;
  const canSeeSettingsPartners = isSuperuser;
  const canRunScans =
    isSuperuser ||
    role === 'partner' ||
    role === 'admin' ||
    role === 'analyst' ||
    effectiveCaps.can_run_scans === true ||
    hasCapability('can_run_scans');
  return {
    role,
    isSuperuser,
    isPartner,
    canRunScans,
    canSeeSubscription: !isPartner && (isSuperuser || role === 'admin'),
    canSeePartnerUi: isPartner,
    canSeeSettingsConnectors,
    canManageSettingsConnectors,
    canSeeSettingsDeployment,
    canSeeSettingsPartners,
    canSeeSettingsManageUsers: canManageUsers,
    canSeeSettingsUsersOrgs: canManageOrganizations,
  };
}

const IconEnvelope = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-settings-input-icon" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-settings-input-icon" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-settings-input-icon profile-settings-eye" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconEyeOff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-settings-input-icon profile-settings-eye" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const IconSmartphone = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="profile-settings-mfa-method-icon-svg"
    aria-hidden="true"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const IconAuthenticator = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="profile-settings-mfa-method-icon-svg"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="3" height="3" rx="0.5" />
    <rect x="18" y="14" width="3" height="3" rx="0.5" />
    <rect x="14" y="18" width="3" height="3" rx="0.5" />
    <rect x="18" y="18" width="3" height="3" rx="0.5" />
  </svg>
);

const IconRecoveryCodes = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="profile-settings-mfa-method-icon-svg"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="14" y2="17" />
  </svg>
);

const IconRegenerate = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const IconDownload = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconCopy = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="profile-settings-mfa-app-secret-copy-icon"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

function normalizeProfileTab(tab) {
  const safe = String(tab || '').trim().toLowerCase();
  if (safe === 'mfa' || safe === 'account_security') return 'account_security';
  if (safe === 'manage_users') return 'manage_users';
  if (safe === 'profile' || safe === 'personal_info') return 'personal_info';
  return 'personal_info';
}

function normalizeMfaMethod(method) {
  const safe = String(method || '').trim().toLowerCase();
  if (safe === 'email' || safe === 'sms') return safe;
  if (safe === 'app' || safe === 'totp' || safe === 'authenticator' || safe === 'auth_app') return 'app';
  if (safe === 'recovery' || safe === 'backup' || safe === 'recovery_codes' || safe === 'backup_codes') return 'recovery';
  return '';
}

function isMfaOtpUiMethod(method) {
  const m = normalizeMfaMethod(method);
  return m === 'email' || m === 'sms';
}

function mfaMethodDisplayLabel(method) {
  const m = normalizeMfaMethod(method);
  if (m === 'email') return 'Email';
  if (m === 'sms') return 'Text message';
  if (m === 'app') return 'Authenticator app';
  if (m === 'recovery') return 'Recovery codes';
  const raw = String(method || '').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'MFA';
}

/** Clean MFA API/error payloads for dialog copy (unwrap list wrappers, friendly expiry text). */
function formatMfaDialogError(raw) {
  let text = String(raw || '').trim();
  const listMatch = text.match(/^\[\s*['"]([\s\S]*?)['"]\s*\]$/);
  if (listMatch) text = String(listMatch[1] || '').trim();
  if (!text) return 'Unable to verify that code. Please try again.';
  if (/otp challenge expired|challenge expired|code has expired|code expired/i.test(text)) {
    return 'This code has expired. Close this window and send a new verification code.';
  }
  if (/invalid|incorrect|wrong|does not match/i.test(text) && /code|otp|token/i.test(text)) {
    return 'That code is incorrect. Check the message and try again.';
  }
  return text.endsWith('.') ? text : `${text}.`;
}

/** Dial prefixes for SMS MFA (E.164); extend as needed. Compact labels keep the select narrow. */
const MFA_SMS_COUNTRY_DIAL_OPTIONS = [
  { dial: '+1', label: '+1 US/CA' },
  { dial: '+61', label: '+61 AU' },
  { dial: '+64', label: '+64 NZ' },
  { dial: '+44', label: '+44 UK' },
  { dial: '+353', label: '+353 IE' },
  { dial: '+91', label: '+91 IN' },
  { dial: '+86', label: '+86 CN' },
  { dial: '+81', label: '+81 JP' },
  { dial: '+82', label: '+82 KR' },
  { dial: '+65', label: '+65 SG' },
  { dial: '+60', label: '+60 MY' },
  { dial: '+63', label: '+63 PH' },
  { dial: '+66', label: '+66 TH' },
  { dial: '+84', label: '+84 VN' },
  { dial: '+49', label: '+49 DE' },
  { dial: '+33', label: '+33 FR' },
  { dial: '+39', label: '+39 IT' },
  { dial: '+34', label: '+34 ES' },
  { dial: '+31', label: '+31 NL' },
  { dial: '+46', label: '+46 SE' },
  { dial: '+47', label: '+47 NO' },
  { dial: '+41', label: '+41 CH' },
  { dial: '+32', label: '+32 BE' },
  { dial: '+43', label: '+43 AT' },
  { dial: '+45', label: '+45 DK' },
  { dial: '+48', label: '+48 PL' },
  { dial: '+351', label: '+351 PT' },
  { dial: '+27', label: '+27 ZA' },
  { dial: '+971', label: '+971 AE' },
  { dial: '+966', label: '+966 SA' },
  { dial: '+52', label: '+52 MX' },
  { dial: '+55', label: '+55 BR' },
  { dial: '+54', label: '+54 AR' },
];

function buildMfaSmsE164(countryDial, nationalRaw) {
  const dialDigits = String(countryDial || '').replace(/\D/g, '');
  let nationalDigits = String(nationalRaw || '').replace(/\D/g, '');
  nationalDigits = nationalDigits.replace(/^0+/, '');
  if (!dialDigits || !nationalDigits) return '';
  return `+${dialDigits}${nationalDigits}`;
}

function parseProfileMobileForMfaSms(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return { dial: '+61', national: '' };
  }
  const withPlus = trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/^\+/, '')}`;
  const allDigits = withPlus.replace(/\D/g, '');
  if (!allDigits) {
    return { dial: '+61', national: '' };
  }
  const byCcLen = [...MFA_SMS_COUNTRY_DIAL_OPTIONS].sort(
    (a, b) => b.dial.replace(/\D/g, '').length - a.dial.replace(/\D/g, '').length
  );
  for (let i = 0; i < byCcLen.length; i += 1) {
    const opt = byCcLen[i];
    const cc = opt.dial.replace(/\D/g, '');
    if (allDigits.startsWith(cc)) {
      return { dial: opt.dial, national: allDigits.slice(cc.length) };
    }
  }
  return { dial: '+61', national: allDigits };
}

/** Visual placeholder QR for TOTP setup until `otpauth://` or enrollment API provides a real image. */
function MfaTotpQrPlaceholder({ size = 200, secret = '' }) {
  const seed = String(secret || 'PLACEHOLDER');
  const { n, keys } = useMemo(() => {
    const gridN = 27;
    const black = new Set();
    const addFinder = (ox, oy) => {
      for (let dy = 0; dy < 7; dy++) {
        for (let dx = 0; dx < 7; dx++) {
          black.add(`${ox + dx},${oy + dy}`);
        }
      }
    };
    addFinder(0, 0);
    addFinder(gridN - 7, 0);
    addFinder(0, gridN - 7);
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    for (let y = 0; y < gridN; y++) {
      for (let x = 0; x < gridN; x++) {
        const k = `${x},${y}`;
        if (black.has(k)) continue;
        if (x < 9 && y < 9) continue;
        if (x >= gridN - 9 && y < 9) continue;
        if (x < 9 && y >= gridN - 9) continue;
        if ((Math.imul(x, 17) ^ Math.imul(y, 31) ^ h) % 3 === 0) black.add(k);
      }
    }
    return { n: gridN, keys: Array.from(black) };
  }, [seed]);
  const cell = size / n;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="profile-settings-mfa-app-qr-svg"
      role="img"
      aria-label="Sample QR code for authenticator setup"
    >
      <rect width={size} height={size} fill="#ffffff" />
      {keys.map((k) => {
        const [x, y] = k.split(',').map(Number);
        return (
          <rect key={k} x={x * cell} y={y * cell} width={cell + 0.02} height={cell + 0.02} fill="#0a0a0a" />
        );
      })}
    </svg>
  );
}

/**
 * Profile Settings: General (name, contact & region, password) and Security & Access (MFA, devices).
 * Uses getProfile / updateProfile; updates bubble to header via onProfileChange.
 */
function manageOrgUuid(org) {
  return String(org?.uu_id || org?.org_uuid || org?.organization_uuid || '').trim().toLowerCase();
}

function manageSiteUuid(site) {
  return String(site?.uu_id || site?.site_uuid || site?.uuid || '').trim().toLowerCase();
}

function manageSiteOrgUuid(site, orgs) {
  const direct = String(site?.org_uuid || site?.organization_uuid || site?.organization_uu_id || '').trim().toLowerCase();
  if (direct) return direct;
  const orgKey = String(site?.organization_id || site?.org_id || site?.organization || site?.organization_name || '').trim();
  const match = (orgs || []).find((org) => {
    const uuid = manageOrgUuid(org);
    return uuid === orgKey.toLowerCase() || String(org?.id || '') === orgKey || String(org?.name || '') === orgKey;
  });
  return match ? manageOrgUuid(match) : '';
}

export default function ProfileSettings({
  userProfile,
  onProfileChange,
  forcedTab = null,
  hideProfileChrome = false,
  onAddInviteUser = null,
  usersRefreshKey = 0,
  onLogout = null,
  headerSelectedOrgId = '',
  headerSelectedOrgName = '',
}) {
  const navigate = useNavigate();
  // Keep a ref so the profile-fetch effect can call the latest onProfileChange
  // without listing it as a dependency (an inline arrow from the parent would
  // change identity on every render and cause an infinite fetch loop).
  const onProfileChangeRef = useRef(onProfileChange);
  onProfileChangeRef.current = onProfileChange;
  const manageUserRowKey = (row) => String(row?.uu_id || row?.email || row?.id || '').trim();
  const [firstName, setFirstName] = useState('');
  const [usersHeaderActionsHost, setUsersHeaderActionsHost] = useState(null);

  useLayoutEffect(() => {
    setUsersHeaderActionsHost(document.getElementById('users-page-header-actions'));
  }, []);
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(userProfile?.email || '');
  const [profileTimezone, setProfileTimezone] = useState(
    String(userProfile?.timezone || '').trim() || getPreferredTimezoneDefault()
  );
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('');
  const passwordChangeLogoutTimerRef = useRef(null);
  const [savedSnapshot, setSavedSnapshot] = useState({
    firstName: '',
    lastName: '',
    email: '',
    timezone: getPreferredTimezoneDefault(),
  });
  const isProfileDirty = useMemo(() => {
    const timezone = String(profileTimezone || '').trim() || getPreferredTimezoneDefault();
    const snapshotTimezone = String(savedSnapshot.timezone || '').trim() || getPreferredTimezoneDefault();
    return (
      firstName.trim() !== String(savedSnapshot.firstName || '').trim()
      || lastName.trim() !== String(savedSnapshot.lastName || '').trim()
      || email.trim() !== String(savedSnapshot.email || '').trim()
      || timezone !== snapshotTimezone
    );
  }, [
    firstName,
    lastName,
    email,
    profileTimezone,
    savedSnapshot,
  ]);
  const isPasswordDirty = useMemo(
    () => Boolean(currentPassword || newPassword || passwordConfirm),
    [currentPassword, newPassword, passwordConfirm]
  );
  const isPasswordFormFilled = useMemo(
    () =>
      Boolean(
        currentPassword.trim() &&
          newPassword.trim() &&
          passwordConfirm.trim()
      ),
    [currentPassword, newPassword, passwordConfirm]
  );
  const [activeTab, setActiveTab] = useState(normalizeProfileTab(forcedTab));

  useEffect(() => {
    const onTab = (event) => {
      const tab = normalizeProfileTab(event?.detail?.tab);
      if (tab === 'personal_info' || tab === 'account_security') setActiveTab(tab);
    };
    window.addEventListener('xspectre:tour-profile-tab', onTab);
    return () => window.removeEventListener('xspectre:tour-profile-tab', onTab);
  }, []);
  const [mfaMethod, setMfaMethod] = useState('email');
  const [mfaSmsDialCode, setMfaSmsDialCode] = useState('+61');
  const [mfaSmsDialOpen, setMfaSmsDialOpen] = useState(false);
  const mfaSmsDialRef = useRef(null);
  const [mfaSmsNationalNumber, setMfaSmsNationalNumber] = useState('');
  const mfaSmsE164Number = useMemo(
    () => buildMfaSmsE164(mfaSmsDialCode, mfaSmsNationalNumber),
    [mfaSmsDialCode, mfaSmsNationalNumber]
  );
  const [mfaChallengeId, setMfaChallengeId] = useState('');
  const [mfaChallengeMethod, setMfaChallengeMethod] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaActiveMethod, setMfaActiveMethod] = useState('');
  const [mfaHasActivePrimary, setMfaHasActivePrimary] = useState(false);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(false);
  const [mfaStatusHydrated, setMfaStatusHydrated] = useState(false);
  const [mfaAppTotpCode, setMfaAppTotpCode] = useState('');
  const [mfaTotpSetup, setMfaTotpSetup] = useState(null);
  const [mfaTotpLoading, setMfaTotpLoading] = useState(false);
  const [mfaTotpSetupConflict, setMfaTotpSetupConflict] = useState(false);
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState([]);
  const [, setMfaRecoveryConfigured] = useState(false);
  const [, setMfaRecoveryUnusedCount] = useState(0);
  const [, setMfaRecoveryTotalCount] = useState(0);
  const [mfaRecoveryLoading, setMfaRecoveryLoading] = useState(false);
  const [mfaRecoveryBusy, setMfaRecoveryBusy] = useState(false);
  const [mfaRecoveryPasswordOpen, setMfaRecoveryPasswordOpen] = useState(false);
  const [mfaRecoveryPassword, setMfaRecoveryPassword] = useState('');
  const [mfaRecoveryPasswordError, setMfaRecoveryPasswordError] = useState('');
  const [mfaDisablePasswordOpen, setMfaDisablePasswordOpen] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisablePasswordError, setMfaDisablePasswordError] = useState('');
  const [mfaDisableBusy, setMfaDisableBusy] = useState(false);
  const [mfaSendCooldownSeconds, setMfaSendCooldownSeconds] = useState(0);
  const mfaSendCooldownTimerRef = useRef(null);
  const profileTimezoneOptions = useMemo(() => getTimezoneOptions(profileTimezone), [profileTimezone]);
  // Dev preview: when backend is not connected yet, allow an explicit local toggle
  // so admins can still see the UI. Backend will override this on success.
  const [canManageUsers, setCanManageUsers] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const qpPreview = url.searchParams.get('admin_preview');
      const byQuery = String(qpPreview).toLowerCase() === 'true';
      const byStorage = localStorage.getItem('xspectre_admin_preview') === 'true';
      if (byQuery || byStorage) return true;
      return buildUiAccessPolicy(userProfile || EMPTY_USER_PROFILE).canSeeSettingsManageUsers;
    } catch (_) {
      return false;
    }
  });
  const [canManageUsersResolved, setCanManageUsersResolved] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const qpPreview = url.searchParams.get('admin_preview');
      const byQuery = String(qpPreview).toLowerCase() === 'true';
      const byStorage = localStorage.getItem('xspectre_admin_preview') === 'true';
      if (byQuery || byStorage) return true;
      // Dashboard already loaded profile for sidebar gating — don't block lists on a second getProfile.
      if (userProfile && (userProfile.role || userProfile.capabilities || userProfile.is_superuser || userProfile.email)) {
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  });
  const [usersRefreshNonce, setUsersRefreshNonce] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [currentUserUuId, setCurrentUserUuId] = useState(() => String(userProfile?.uu_id || '').trim());
  const [currentUserEmailLower, setCurrentUserEmailLower] = useState(
    () => String(userProfile?.email || '').trim().toLowerCase()
  );
  const [usersFilterRole, setUsersFilterRole] = useState('');
  const [usersFilterOrg, setUsersFilterOrg] = useState(() => String(headerSelectedOrgName || '').trim());
  const [usersFilterSearchQuery, setUsersFilterSearchQuery] = useState('');
  const [usersFilterRoleOpen, setUsersFilterRoleOpen] = useState(false);
  const [selectedManageUserKeys, setSelectedManageUserKeys] = useState([]);
  const [openManageUserRowMenuKey, setOpenManageUserRowMenuKey] = useState(null);
  const [manageUserRowMenuPlacement, setManageUserRowMenuPlacement] = useState(null);
  const manageUserRowActionsMenuRef = useRef(null);
  const manageUserRowActionsTriggerRef = useRef(null);
  const usersFilterRoleRef = useRef(null);
  const [usersActionLoading, setUsersActionLoading] = useState(false);
  const [usersActionMessage, setUsersActionMessage] = useState('');
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    role: '2',
    organization: '',
    timezone: getPreferredTimezoneDefault(),
  });

  const [orgsLoaded, setOrgsLoaded] = useState(
    () => Array.isArray(queryClient.getQueryData(manageUsersOrgsCacheKey()))
  );
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState(null);
  const [orgsList, setOrgsList] = useState(
    () => queryClient.getQueryData(manageUsersOrgsCacheKey()) || []
  );
  const [manageUserSites, setManageUserSites] = useState(
    () => queryClient.getQueryData(manageUsersSitesCacheKey()) || []
  );
  const [manageUserOrgSearch, setManageUserOrgSearch] = useState('');
  const [manageUserSiteSearch, setManageUserSiteSearch] = useState('');

  useEffect(() => {
    let isMounted = true;
    const manageUsersOnly = hideProfileChrome && forcedTab === 'manage_users';
    const hasDashboardProfile = Boolean(
      userProfile && (userProfile.role || userProfile.capabilities || userProfile.is_superuser || userProfile.email)
    );

    if (manageUsersOnly && hasDashboardProfile) {
      const accessPolicy = buildUiAccessPolicy(userProfile || EMPTY_USER_PROFILE);
      setCanManageUsers(Boolean(accessPolicy.canSeeSettingsManageUsers));
      setCanManageUsersResolved(true);
      setCurrentUserUuId(String(userProfile?.uu_id || '').trim());
      setCurrentUserEmailLower(String(userProfile?.email || '').trim().toLowerCase());
      const fullName = String(userProfile?.name || '').trim();
      const parts = fullName.split(/\s+/).filter(Boolean);
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
      setEmail(String(userProfile?.email || '').trim());
      return () => { isMounted = false; };
    }

    getProfile()
      .then((data) => {
        if (!isMounted) return;
        const fullName = data.name ?? data.display_name ?? '';
        const parts = fullName.trim().split(/\s+/);
        const first = parts[0] ?? '';
        const last = parts.slice(1).join(' ') ?? '';
        const profileUuId = String(data.uu_id ?? data.uuId ?? '').trim();
        setFirstName(first);
        setLastName(last);
        setEmail(data.email ?? '');
        setProfileTimezone(String(data?.timezone || '').trim() || getPreferredTimezoneDefault());
        const mfaMobile = parseProfileMobileForMfaSms(data.mobile_number);
        setMfaSmsDialCode(mfaMobile.dial);
        setMfaSmsNationalNumber(mfaMobile.national);
        setSavedSnapshot({
          firstName: first,
          lastName: last,
          email: data.email ?? '',
          timezone: String(data?.timezone || '').trim() || getPreferredTimezoneDefault(),
        });
        setCurrentUserUuId(profileUuId);
        setCurrentUserEmailLower(String(data.email ?? '').trim().toLowerCase());

        // Manage-users gating  align with sidebar via buildUiAccessPolicy (includes partners).
        const mappedProfile = mapApiProfileToUserProfile(data, EMPTY_USER_PROFILE);
        const accessPolicy = buildUiAccessPolicy(mappedProfile);
        const adminByStaff = data.is_staff === true || data.is_staff === 'true';
        const adminByExplicitFlag = data.isAdmin === true || data.is_admin === true;
        const canManageFromProfile = accessPolicy.canSeeSettingsManageUsers || adminByStaff || adminByExplicitFlag;
        setCanManageUsers(Boolean(canManageFromProfile));

        // If backend doesn't include role/capabilities in /api/profile/, probe the users endpoint.
        const roleRaw = data.role ?? '';
        const roleStr =
          typeof roleRaw === 'string'
            ? roleRaw
            : (roleRaw?.role ?? roleRaw?.name ?? roleRaw?.label ?? '');
        const roleInfoPresent = roleStr !== '' && roleStr !== undefined && roleStr !== null;
        const capsInfoPresent = Boolean(data?.capabilities?.effective_capabilities);
        const staffInfoPresent = typeof data.is_staff !== 'undefined';
        const explicitAdminInfoPresent = typeof data.isAdmin !== 'undefined' || typeof data.is_admin !== 'undefined';
        const unknownCanManageUsers =
          !canManageFromProfile &&
          !roleInfoPresent &&
          !capsInfoPresent &&
          !staffInfoPresent &&
          !explicitAdminInfoPresent &&
          profileUuId;

        if (unknownCanManageUsers) {
          listUsersAdmin(null, { uu_id: profileUuId })
            .then(() => {
              if (!isMounted) return;
              setCanManageUsers(true);
            })
            .catch(() => {
              // Users without can_manage_users will get 403/401; keep Manage users hidden.
            })
            .finally(() => {
              if (!isMounted) return;
              setCanManageUsersResolved(true);
            });
        } else {
          setCanManageUsersResolved(true);
        }

        if (onProfileChangeRef.current) {
          onProfileChangeRef.current({
            name: fullName,
            email: data.email ?? '',
            timezone: String(data?.timezone || '').trim() || getPreferredTimezoneDefault(),
          });
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setLoadError(err.message || 'Failed to load profile');
        // If backend/profile can't be loaded, keep whatever admin preview flag is set.
        setCanManageUsersResolved(true);
      });
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canManageUsersResolved) return;
    if (!canManageUsers && activeTab === 'manage_users') {
      setActiveTab(normalizeProfileTab(forcedTab));
    }
  }, [canManageUsersResolved, canManageUsers, activeTab, forcedTab]);

  useEffect(() => {
    if (!forcedTab) return;
    const nextTab = normalizeProfileTab(forcedTab);
    if (activeTab !== nextTab) setActiveTab(nextTab);
  }, [forcedTab, activeTab]);

  useEffect(() => {
    if (!mfaSmsDialOpen) return undefined;
    const onDoc = (e) => {
      const el = mfaSmsDialRef.current;
      if (el && !el.contains(e.target)) setMfaSmsDialOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMfaSmsDialOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [mfaSmsDialOpen]);

  useEffect(() => {
    if (mfaMethod !== 'sms') setMfaSmsDialOpen(false);
  }, [mfaMethod]);

  useEffect(() => {
    if (!usersFilterRoleOpen) return;
    const onDoc = (e) => {
      const el = usersFilterRoleRef.current;
      if (el && !el.contains(e.target)) setUsersFilterRoleOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [usersFilterRoleOpen]);

  const usersSelectedOrgApiId = useMemo(() => {
    if (!usersFilterOrg) return '';
    const match = orgsList.find((org) => {
      const name = String(org?.name || org?.org_name || '').trim();
      return name === usersFilterOrg;
    });
    const fromList = String(match?.id || match?.organization_id || match?.uu_id || match?.org_uuid || '').trim();
    if (fromList) return fromList;
    if (
      headerSelectedOrgId &&
      headerSelectedOrgName &&
      usersFilterOrg === headerSelectedOrgName
    ) {
      return String(headerSelectedOrgId).trim();
    }
    return '';
  }, [usersFilterOrg, orgsList, headerSelectedOrgId, headerSelectedOrgName]);

  useEffect(() => {
    if (activeTab !== 'manage_users') return;
    setUsersFilterOrg(String(headerSelectedOrgName || '').trim());
  }, [activeTab, headerSelectedOrgId, headerSelectedOrgName]);

  const usersListApiQuery = useMemo(() => {
    if (!usersSelectedOrgApiId) return null;
    return { organization: usersSelectedOrgApiId };
  }, [usersSelectedOrgApiId]);

  const lastUsersRefreshNonceRef = useRef(usersRefreshNonce);

  const isSelfManageUser = useCallback((row) => {
    const rowUuId = String(row?.uu_id || row?.uuId || '').trim();
    if (currentUserUuId && rowUuId && rowUuId === currentUserUuId) return true;
    const rowEmail = String(row?.email || '').trim().toLowerCase();
    return Boolean(currentUserEmailLower && rowEmail && rowEmail === currentUserEmailLower);
  }, [currentUserUuId, currentUserEmailLower]);

  const profileSettingsPath = useMemo(() => pageToPath('profileSettings'), []);
  const goToProfileSettings = useCallback(() => {
    navigate(profileSettingsPath);
  }, [navigate, profileSettingsPath]);

  useEffect(() => {
    if (!canManageUsers || activeTab !== 'manage_users') return;
    let isMounted = true;
    const cacheKey = manageUsersCacheKey(usersListApiQuery?.organization);
    const cached = queryClient.getQueryData(cacheKey);
    const forceRefresh = lastUsersRefreshNonceRef.current !== usersRefreshNonce;
    lastUsersRefreshNonceRef.current = usersRefreshNonce;

    if (cached && !forceRefresh) {
      setUsersList(Array.isArray(cached) ? cached : []);
      setUsersLoading(false);
      setUsersError(null);
      if (manageUsersCacheIsFresh(cacheKey)) {
        return () => { isMounted = false; };
      }
    } else if (!cached) {
      setUsersLoading(true);
    }

    setUsersError(null);

    listUsersAdmin(null, usersListApiQuery)
      .then((data) => {
        if (!isMounted) return;
        const rows = Array.isArray(data) ? data : [];
        setUsersList(rows);
        queryClient.setQueryData(cacheKey, rows);
      })
      .catch((err) => {
        if (!isMounted) return;
        setUsersError(err.message || 'Failed to load users');
      })
      .finally(() => {
        if (!isMounted) return;
        setUsersLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeTab, canManageUsers, usersRefreshNonce, usersListApiQuery]);

  useEffect(() => {
    if (!canManageUsers || activeTab !== 'manage_users' || orgsLoaded) return;
    let isMounted = true;
    const cached = queryClient.getQueryData(manageUsersOrgsCacheKey());

    if (cached && manageUsersCacheIsFresh(manageUsersOrgsCacheKey())) {
      setOrgsList(Array.isArray(cached) ? cached : []);
      setOrgsLoaded(true);
      setOrgsLoading(false);
      return () => { isMounted = false; };
    }

    setOrgsLoading(true);
    setOrgsError(null);

    Promise.allSettled([listOrganizations()])
      .then((results) => {
        if (!isMounted) return;

        const orgResult = results[0];

        if (orgResult.status === 'fulfilled') {
          const rows = Array.isArray(orgResult.value)
            ? orgResult.value
            : Array.isArray(orgResult.value?.results)
              ? orgResult.value.results
              : [];
          setOrgsList(rows);
          setOrgsLoaded(true);
          queryClient.setQueryData(manageUsersOrgsCacheKey(), rows);
          if (!newUserForm.organization && rows.length > 0) {
            const firstOrgId = rows[0]?.id || rows[0]?.organization_id || rows[0]?.uu_id || rows[0]?.org_uuid || '';
            if (firstOrgId) {
              setNewUserForm((prev) => ({ ...prev, organization: String(firstOrgId) }));
            }
          }
        } else {
          setOrgsError(orgResult.reason?.message || 'Failed to load organizations');
        }
      })
      .finally(() => {
        if (!isMounted) return;
        setOrgsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeTab, canManageUsers, orgsLoaded, newUserForm.organization]);

  useEffect(() => {
    if (!canManageUsers || activeTab !== 'manage_users') return undefined;
    let isMounted = true;
    const cached = queryClient.getQueryData(manageUsersSitesCacheKey());
    if (cached && manageUsersCacheIsFresh(manageUsersSitesCacheKey())) {
      setManageUserSites(Array.isArray(cached) ? cached : []);
      return () => { isMounted = false; };
    }

    getSites()
      .then((rows) => {
        if (!isMounted) return;
        const next = Array.isArray(rows) ? rows : [];
        setManageUserSites(next);
        queryClient.setQueryData(manageUsersSitesCacheKey(), next);
      })
      .catch(() => {
        if (!isMounted) return;
        setManageUserSites([]);
      });
    return () => {
      isMounted = false;
    };
  }, [activeTab, canManageUsers]);

  const usersRoleFilterOptions = useMemo(() => {
    const uniq = new Set(
      usersList
        .map((u) => String(u?.role || '').trim())
        .filter(Boolean)
    );
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }, [usersList]);

  const filteredUsers = useMemo(() => {
    const q = String(usersFilterSearchQuery || '').trim().toLowerCase();
    return usersList.filter((u) => {
      const role = String(u?.role || '').trim();
      const org = String(u?.organization || '').trim();
      if (usersFilterRole && role !== usersFilterRole) return false;
      if (usersFilterOrg && org !== usersFilterOrg) return false;
      if (q) {
        const nameText = String(u?.name || '').toLowerCase();
        const emailText = String(u?.email || '').toLowerCase();
        const roleText = String(u?.role || '').toLowerCase();
        const orgText = String(u?.organization || '').toLowerCase();
        if (
          !nameText.includes(q) &&
          !emailText.includes(q) &&
          !roleText.includes(q) &&
          !orgText.includes(q)
        ) return false;
      }
      return true;
    });
  }, [usersList, usersFilterRole, usersFilterOrg, usersFilterSearchQuery]);

  const [manageUsersSortConfig, setManageUsersSortConfig] = useState({ key: 'name', direction: 'asc' });

  const handleManageUsersSort = useCallback((key) => {
    setManageUsersSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getManageUsersSortIcon = useCallback(
    (key) => getScansStyleSortIcon(manageUsersSortConfig, key),
    [manageUsersSortConfig]
  );

  const sortedFilteredUsers = useMemo(() => {
    const sorted = stableSortTableRows(filteredUsers, manageUsersSortConfig, (a, b, key) => {
      if (key === 'name') return compareLocaleStrings(a.name || a.email, b.name || b.email);
      if (key === 'email') return compareLocaleStrings(a.email, b.email);
      if (key === 'role') return compareLocaleStrings(a.role, b.role);
      if (key === 'organization') return compareLocaleStrings(a.organization, b.organization);
      return 0;
    });
    const selfRows = [];
    const otherRows = [];
    sorted.forEach((row) => {
      if (isSelfManageUser(row)) selfRows.push(row);
      else otherRows.push(row);
    });
    if (selfRows.length === 0) {
      usersList.forEach((row) => {
        if (isSelfManageUser(row)) selfRows.push(row);
      });
    }
    return [...selfRows, ...otherRows];
  }, [filteredUsers, manageUsersSortConfig, isSelfManageUser, usersList]);

  const manageUsersPagination = usePaginatedRows(sortedFilteredUsers, TABLE_PAGE_SIZE);
  const visibleManageUserRows = manageUsersPagination.pageRows;

  const visibleManageUserKeys = useMemo(
    () => visibleManageUserRows.filter((u) => !isSelfManageUser(u)).map((u) => manageUserRowKey(u)).filter(Boolean),
    [visibleManageUserRows, isSelfManageUser]
  );
  const allVisibleManageUsersSelected = visibleManageUserKeys.length > 0 &&
    visibleManageUserKeys.every((k) => selectedManageUserKeys.includes(k));

  useEffect(() => {
    const allowed = new Set(
      usersList
        .filter((u) => !isSelfManageUser(u))
        .map((u) => manageUserRowKey(u))
        .filter(Boolean)
    );
    setSelectedManageUserKeys((prev) => prev.filter((k) => allowed.has(k)));
  }, [usersList, isSelfManageUser]);

  const toggleSelectAllVisibleManageUsers = useCallback(() => {
    setSelectedManageUserKeys((prev) => {
      if (allVisibleManageUsersSelected) {
        const visibleSet = new Set(visibleManageUserKeys);
        return prev.filter((k) => !visibleSet.has(k));
      }
      const next = new Set(prev);
      visibleManageUserKeys.forEach((k) => next.add(k));
      return Array.from(next);
    });
  }, [allVisibleManageUsersSelected, visibleManageUserKeys]);

  const manageUsersTableColumns = useMemo(() => {
    const cols = MANAGE_USERS_TABLE_COLUMNS.map((col) => ({ ...col }));
    const checkboxCol = cols.find((col) => col.key === 'checkbox');
    if (checkboxCol) {
      checkboxCol.headerContent = (
        <input
          type="checkbox"
          className="risks-page-checkbox users-orgs-settings-checkbox"
          checked={allVisibleManageUsersSelected}
          disabled={visibleManageUserKeys.length === 0}
          onChange={toggleSelectAllVisibleManageUsers}
          aria-label="Select all users in this list"
        />
      );
    }
    return cols;
  }, [allVisibleManageUsersSelected, visibleManageUserKeys.length, toggleSelectAllVisibleManageUsers]);

  const {
    widths: manageUsersColumnWidths,
    onResizeStart: onManageUsersColumnResizeStart,
    tableMinWidth: manageUsersTableMinWidth,
    resizingKey: manageUsersColumnResizingKey,
  } = useTableColumnResize(manageUsersTableColumns, MANAGE_USERS_TABLE_COLUMN_WIDTHS_KEY);

  const manageUsersTableHeaderRef = useRef(null);
  const manageUsersTableScrollRef = useRef(null);
  const [manageUsersHeaderGutterPx, setManageUsersHeaderGutterPx] = useState(8);

  const handleManageUsersTableScroll = useCallback((event) => {
    if (manageUsersTableHeaderRef.current) {
      manageUsersTableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    if (activeTab !== 'manage_users') return undefined;
    const el = manageUsersTableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setManageUsersHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [activeTab, usersLoading, sortedFilteredUsers.length, filteredUsers.length]);

  const manageUsersTableClassName =
    'assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table manage-users-table';
  const manageUsersTableStyle = { width: '100%', minWidth: manageUsersTableMinWidth };

  const toggleManageUserRowSelected = useCallback((row) => {
    const key = manageUserRowKey(row);
    if (!key) return;
    setSelectedManageUserKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const handleCancelEdit = () => {
    setFirstName(savedSnapshot.firstName);
    setLastName(savedSnapshot.lastName);
    setEmail(savedSnapshot.email);
    setProfileTimezone(String(savedSnapshot.timezone || '').trim() || getPreferredTimezoneDefault());
    setSubmitError(null);
    setSubmitSuccessMessage('');
    setIsEditingProfile(false);
  };

  const handleClearPassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setPasswordConfirm('');
    setSubmitError(null);
    setSubmitSuccessMessage('');
    setIsEditingPassword(false);
  };

  useEffect(() => () => {
    if (passwordChangeLogoutTimerRef.current) {
      window.clearTimeout(passwordChangeLogoutTimerRef.current);
      passwordChangeLogoutTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isProfileDirty) return;
    setSubmitSuccessMessage((current) => (
      current === 'Profile updated successfully.' ? '' : current
    ));
  }, [isProfileDirty]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccessMessage('');
    setLoading(true);
    const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    const payload = {
      name: name || undefined,
      email: email.trim() || undefined,
      timezone: String(profileTimezone || '').trim() || getPreferredTimezoneDefault(),
    };

    updateProfile(payload)
      .then(() => {
        const newEmail = email.trim() || userProfile?.email || '';
        if (onProfileChange) {
          onProfileChange({
            name: name || userProfile?.name || '',
            email: newEmail,
            timezone: String(profileTimezone || '').trim() || getPreferredTimezoneDefault(),
          });
        }
        setSavedSnapshot({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: newEmail,
          timezone: String(profileTimezone || '').trim() || getPreferredTimezoneDefault(),
        });
        setSubmitSuccessMessage('Profile updated successfully.');
        setIsEditingProfile(false);
      })
      .catch((err) => {
        setSubmitError(err.message || 'Update failed');
      })
      .finally(() => setLoading(false));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccessMessage('');
    if (!newPassword) {
      setSubmitError('Enter a new password to continue');
      return;
    }
    if (newPassword !== passwordConfirm) {
      setSubmitError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }
    if (!currentPassword) {
      setSubmitError('Current password is required to change your password');
      return;
    }

    setLoading(true);
    const snapshotName = [savedSnapshot.firstName, savedSnapshot.lastName]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' ');
    const payload = {
      name: snapshotName || undefined,
      email: String(savedSnapshot.email || '').trim() || undefined,
      timezone: String(savedSnapshot.timezone || '').trim() || getPreferredTimezoneDefault(),
      password: newPassword,
      password_confirm: passwordConfirm,
      current_password: currentPassword,
    };

    updateProfile(payload)
      .then(() => {
        setCurrentPassword('');
        setNewPassword('');
        setPasswordConfirm('');
        setIsEditingPassword(false);
        setSubmitSuccessMessage('Password updated successfully. Redirecting to sign in...');
        if (typeof onLogout === 'function') {
          if (passwordChangeLogoutTimerRef.current) {
            window.clearTimeout(passwordChangeLogoutTimerRef.current);
          }
          passwordChangeLogoutTimerRef.current = window.setTimeout(() => {
            passwordChangeLogoutTimerRef.current = null;
            onLogout('password_changed');
          }, 1200);
        }
      })
      .catch((err) => {
        setSubmitError(err.message || 'Update failed');
      })
      .finally(() => setLoading(false));
  };

  const isMfaMethodBlocked = useCallback((method) => {
    const normalized = normalizeMfaMethod(method);
    if (normalized === 'recovery') {
      return false;
    }
    if (mfaHasActivePrimary && !mfaActiveMethod) {
      return normalized === 'email' || normalized === 'sms' || normalized === 'app';
    }
    return Boolean(mfaActiveMethod && normalized && normalized !== mfaActiveMethod);
  }, [mfaActiveMethod, mfaHasActivePrimary]);

  const renderMfaEnrolledBadge = useCallback((methodKey) => {
    if (mfaStatusLoading || normalizeMfaMethod(mfaActiveMethod) !== methodKey) {
      return null;
    }
    return (
      <span className="profile-settings-mfa-method-active-badge" aria-label="Active MFA method">
        Active
      </span>
    );
  }, [mfaStatusLoading, mfaActiveMethod]);

  const hydrateMfaStatus = useCallback(async () => {
    setMfaStatusLoading(true);
    try {
      const statusData = await getMfaStatus();
      const activeMethod = normalizeMfaMethod(
        statusData?.active_method || statusData?.active_methods?.[0]
      );
      const hasActivePrimary = Boolean(
        activeMethod ||
        (Array.isArray(statusData?.active_methods) && statusData.active_methods.length > 0) ||
        statusData?.can_disable
      );
      const preferred = normalizeMfaMethod(statusData?.preferred_otp_channel);
      setMfaHasActivePrimary(hasActivePrimary);
      setMfaActiveMethod((prev) => activeMethod || prev || '');
      if (activeMethod || mfaActiveMethod) {
        setMfaMethod(activeMethod || mfaActiveMethod);
      } else if (preferred) {
        setMfaMethod(preferred);
      } else {
        setMfaMethod('email');
      }
    } catch {
      // Keep UI usable even if status endpoint is temporarily unavailable.
    } finally {
      setMfaStatusLoading(false);
      setMfaStatusHydrated(true);
    }
  }, [mfaActiveMethod]);

  const hydrateRecoveryCodes = useCallback(async () => {
    setMfaRecoveryLoading(true);
    try {
      const data = await getMfaRecoveryCodes();
      const payload = data?.recovery_codes || {};
      const unusedCodes = Array.isArray(payload?.unused_codes) ? payload.unused_codes : [];
      setMfaRecoveryCodes(unusedCodes);
      setMfaRecoveryConfigured(Boolean(payload?.configured));
      setMfaRecoveryUnusedCount(Number(payload?.unused_code_count || 0));
      setMfaRecoveryTotalCount(Number(payload?.total_code_count || 0));
    } catch (err) {
      setMfaRecoveryCodes([]);
      setMfaRecoveryConfigured(false);
      setMfaRecoveryUnusedCount(0);
      setMfaRecoveryTotalCount(0);
      setMfaError((prev) => prev || err?.message || 'Unable to load recovery codes.');
    } finally {
      setMfaRecoveryLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrateMfaStatus();
  }, [hydrateMfaStatus]);

  useEffect(() => {
    if (!mfaStatusHydrated) return;
    if (normalizeMfaMethod(mfaMethod) !== 'recovery') return;
    hydrateRecoveryCodes();
  }, [mfaMethod, mfaStatusHydrated, hydrateRecoveryCodes]);

  useEffect(() => {
    if (mfaSendCooldownSeconds <= 0) {
      if (mfaSendCooldownTimerRef.current) {
        window.clearInterval(mfaSendCooldownTimerRef.current);
        mfaSendCooldownTimerRef.current = null;
      }
      return undefined;
    }

    if (!mfaSendCooldownTimerRef.current) {
      mfaSendCooldownTimerRef.current = window.setInterval(() => {
        setMfaSendCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
      }, 1000);
    }

    return () => {
      if (mfaSendCooldownTimerRef.current) {
        window.clearInterval(mfaSendCooldownTimerRef.current);
        mfaSendCooldownTimerRef.current = null;
      }
    };
  }, [mfaSendCooldownSeconds]);

  const hydrateTotpSetup = useCallback(async () => {
    if (isMfaMethodBlocked('app')) {
      setMfaTotpSetup(null);
      setMfaTotpSetupConflict(true);
      return;
    }

    if (normalizeMfaMethod(mfaActiveMethod) === 'app') {
      // Backend returns 409 when TOTP is already active; avoid redundant setup requests.
      setMfaTotpSetup(null);
      setMfaTotpSetupConflict(false);
      return;
    }

    setMfaTotpLoading(true);
    setMfaTotpSetupConflict(false);
    setMfaError('');
    try {
      // Re-check status just-in-time to avoid avoidable setup conflicts after recent MFA changes.
      const latestStatus = await getMfaStatus();
      const latestActiveMethod = normalizeMfaMethod(
        latestStatus?.active_method || latestStatus?.active_methods?.[0] || mfaActiveMethod
      );
      const latestHasActivePrimary = Boolean(
        latestActiveMethod ||
        (Array.isArray(latestStatus?.active_methods) && latestStatus.active_methods.length > 0) ||
        latestStatus?.can_disable
      );
      setMfaActiveMethod(latestActiveMethod);
      setMfaHasActivePrimary(latestHasActivePrimary);

      if (latestHasActivePrimary && latestActiveMethod !== 'app') {
        setMfaTotpSetup(null);
        setMfaTotpSetupConflict(true);
        setMfaError('Another MFA method is already active. Disable it before setting up Authenticator app.');
        return;
      }

      if (latestActiveMethod === 'app') {
        setMfaTotpSetup(null);
        setMfaTotpSetupConflict(false);
        return;
      }

      const data = await getTotpSetup();
      setMfaTotpSetup({
        secret: data?.secret || '',
        totpUrl: data?.totp_url || '',
        totpSvgDataUri: data?.totp_svg_data_uri || '',
      });
    } catch (err) {
      // On 409 Conflict, TOTP is already active; refresh MFA status to sync frontend state.
      if (err.status === 409) {
        setMfaTotpSetupConflict(true);
        setMfaError('Another MFA method is already active. Disable it before setting up Authenticator app.');
        await hydrateMfaStatus();
      } else {
        setMfaError(err?.message || 'Unable to load authenticator setup.');
      }
      setMfaTotpSetup(null);
    } finally {
      setMfaTotpLoading(false);
    }
  }, [isMfaMethodBlocked, mfaActiveMethod, hydrateMfaStatus]);

  useEffect(() => {
    if (normalizeMfaMethod(mfaMethod) !== 'app') {
      setMfaTotpSetupConflict(false);
      return;
    }

    // Clear stale conflict lock when app setup becomes eligible again.
    if (!isMfaMethodBlocked('app')) {
      setMfaTotpSetupConflict(false);
    }
  }, [mfaMethod, mfaActiveMethod, isMfaMethodBlocked]);

  useEffect(() => {
    if (!isMfaOtpUiMethod(mfaMethod)) {
      setMfaChallengeId('');
      setMfaChallengeMethod('');
      setMfaCode('');
    }
    if (normalizeMfaMethod(mfaMethod) !== 'app') {
      setMfaAppTotpCode('');
    }
    if (
      mfaStatusHydrated &&
      normalizeMfaMethod(mfaMethod) === 'app' &&
      normalizeMfaMethod(mfaActiveMethod) !== 'app' &&
      !mfaTotpSetupConflict &&
      !mfaTotpSetup &&
      !mfaTotpLoading
    ) {
      hydrateTotpSetup();
    }
  }, [mfaMethod, mfaActiveMethod, mfaStatusHydrated, mfaTotpSetupConflict, mfaTotpSetup, mfaTotpLoading, hydrateTotpSetup]);

  const clearMfaOtpChallenge = useCallback(() => {
    setMfaChallengeId('');
    setMfaChallengeMethod('');
    setMfaCode('');
  }, []);

  const handleMfaSendCode = useCallback(async () => {
    const selectedMethod = normalizeMfaMethod(mfaMethod);
    if (!selectedMethod) {
      setMfaError('Select an MFA method first.');
      return;
    }
    if (!isMfaOtpUiMethod(selectedMethod)) {
      setMfaError('');
      setMfaMessage('');
      return;
    }
    if (mfaSendCooldownSeconds > 0) {
      setMfaError(`Please wait ${mfaSendCooldownSeconds}s before requesting another code.`);
      return;
    }
    if (isMfaMethodBlocked(selectedMethod)) {
      setMfaError(
        `Disable ${mfaMethodDisplayLabel(mfaActiveMethod)} MFA before enabling ${mfaMethodDisplayLabel(selectedMethod)}.`
      );
      return;
    }

    if (selectedMethod === 'sms') {
      const nationalDigits = String(mfaSmsNationalNumber || '')
        .replace(/\D/g, '')
        .replace(/^0+/, '');
      if (!nationalDigits || nationalDigits.length < 6) {
        setMfaError('Choose a country code and enter your mobile number (without the country prefix).');
        return;
      }
    }

    setMfaBusy(true);
    setMfaError('');
    setMfaMessage('');
    setMfaChallengeId('');
    setMfaChallengeMethod('');
    setMfaCode('');

    try {
      const result = await sendMfaOtpChallenge({
        mfaType: selectedMethod,
        phoneNumber: selectedMethod === 'sms' ? mfaSmsE164Number : '',
      });
      const challengeId = String(result?.challenge_id || '').trim();
      if (!challengeId) {
        throw new Error('MFA challenge response did not include challenge_id.');
      }
      setMfaChallengeId(challengeId);
      setMfaChallengeMethod(selectedMethod);
      setMfaMessage('');
      if (result?.retryAfterSeconds) {
        setMfaSendCooldownSeconds(Math.ceil(Number(result.retryAfterSeconds) || 0));
      }
    } catch (err) {
      if (err?.retryAfterSeconds) {
        setMfaSendCooldownSeconds(Math.ceil(Number(err.retryAfterSeconds) || 0));
      }
      setMfaError(err?.message || 'Unable to send MFA verification code.');
    } finally {
      setMfaBusy(false);
    }
  }, [mfaMethod, mfaSmsE164Number, mfaSmsNationalNumber, isMfaMethodBlocked, mfaActiveMethod, mfaSendCooldownSeconds]);

  const handleMfaVerifyCode = useCallback(async () => {
    const challengeMethod = normalizeMfaMethod(mfaChallengeMethod || mfaMethod);
    if (!isMfaOtpUiMethod(challengeMethod)) {
      setMfaError('Use email or SMS to complete verification here.');
      return;
    }
    if (!mfaChallengeId || !mfaCode.trim()) {
      setMfaError('Challenge and verification code are required.');
      return;
    }
    if (mfaCode.replace(/\D/g, '').length < MFA_OTP_SLOT_COUNT) {
      setMfaError(`Enter the ${MFA_OTP_SLOT_COUNT}-digit verification code.`);
      return;
    }

    setMfaBusy(true);
    setMfaError('');
    setMfaMessage('');

    try {
      const result = await verifyMfaOtpChallenge({
        mfaType: challengeMethod,
        challengeId: mfaChallengeId,
        code: mfaCode,
        phoneNumber: challengeMethod === 'sms' ? mfaSmsE164Number : '',
      });
      setMfaMessage(result?.message || `${mfaMethodDisplayLabel(challengeMethod)} MFA is now active.`);
      setMfaChallengeId('');
      setMfaChallengeMethod('');
      setMfaCode('');
      await hydrateMfaStatus();
      await hydrateRecoveryCodes();
    } catch (err) {
      setMfaError(err?.message || 'Unable to verify MFA code.');
    } finally {
      setMfaBusy(false);
    }
  }, [mfaMethod, mfaChallengeMethod, mfaChallengeId, mfaCode, mfaSmsE164Number, hydrateMfaStatus, hydrateRecoveryCodes]);

  const openDisablePasswordPrompt = useCallback(() => {
    setMfaDisablePassword('');
    setMfaDisablePasswordError('');
    setMfaDisablePasswordOpen(true);
  }, []);

  const closeDisablePasswordPrompt = useCallback(() => {
    if (mfaDisableBusy) return;
    setMfaDisablePassword('');
    setMfaDisablePasswordError('');
    setMfaDisablePasswordOpen(false);
  }, [mfaDisableBusy]);

  const handleMfaDisable = useCallback(async (event) => {
    event?.preventDefault();
    const password = mfaDisablePassword;
    if (!password) {
      setMfaDisablePasswordError('Enter your current password.');
      return;
    }

    const target = normalizeMfaMethod(mfaActiveMethod || 'active') || 'active';
    setMfaDisableBusy(true);
    setMfaDisablePasswordError('');
    setMfaError('');
    setMfaMessage('');
    try {
      const result = await disableMfaMethod(target, password);
      const nextStatus = result?.status || null;
      const nextActive = normalizeMfaMethod(nextStatus?.active_method);
      setMfaActiveMethod(nextActive);
      setMfaDisablePassword('');
      setMfaDisablePasswordOpen(false);
      setMfaMessage(result?.message || 'MFA method disabled successfully.');
      if (!nextActive) {
        setMfaMethod('email');
      }
      await hydrateMfaStatus();
    } catch (err) {
      setMfaDisablePasswordError(err?.message || 'Unable to disable MFA method.');
    } finally {
      setMfaDisableBusy(false);
    }
  }, [mfaActiveMethod, mfaDisablePassword, hydrateMfaStatus]);

  const handleMfaAppActivate = useCallback(async () => {
    const code = mfaAppTotpCode.trim();
    if (!code) {
      setMfaError('Enter the verification code from your authenticator app.');
      setMfaMessage('');
      return;
    }

    const secret = String(mfaTotpSetup?.secret || '').trim();
    if (!secret) {
      setMfaError('Authenticator setup data is missing. Reload and try again.');
      setMfaMessage('');
      return;
    }

    setMfaBusy(true);
    setMfaError('');
    setMfaMessage('');
    try {
      const result = await activateTotpCode(secret, code);
      setMfaAppTotpCode('');
      setMfaTotpSetup(null);
      setMfaMessage(result?.message || 'Authenticator app MFA is now active.');
      await hydrateMfaStatus();
      await hydrateRecoveryCodes();
    } catch (err) {
      setMfaError(err?.message || 'Unable to activate authenticator. Check the code and try again.');
    } finally {
      setMfaBusy(false);
    }
  }, [mfaAppTotpCode, mfaTotpSetup, hydrateMfaStatus, hydrateRecoveryCodes]);

  const openRecoveryPasswordPrompt = useCallback(() => {
    setMfaRecoveryPassword('');
    setMfaRecoveryPasswordError('');
    setMfaRecoveryPasswordOpen(true);
  }, []);

  const closeRecoveryPasswordPrompt = useCallback(() => {
    if (mfaRecoveryBusy) return;
    setMfaRecoveryPassword('');
    setMfaRecoveryPasswordError('');
    setMfaRecoveryPasswordOpen(false);
  }, [mfaRecoveryBusy]);

  const handleGenerateRecoveryCodes = useCallback(async (event) => {
    event?.preventDefault();
    const password = mfaRecoveryPassword;
    if (!password) {
      setMfaRecoveryPasswordError('Enter your current password.');
      return;
    }

    setMfaRecoveryBusy(true);
    setMfaRecoveryPasswordError('');
    setMfaMessage('');
    try {
      const result = await generateMfaRecoveryCodes(password);
      const payload = result?.recovery_codes || {};
      const unusedCodes = Array.isArray(payload?.unused_codes) ? payload.unused_codes : [];
      setMfaRecoveryCodes(unusedCodes);
      setMfaRecoveryConfigured(Boolean(payload?.configured));
      setMfaRecoveryUnusedCount(Number(payload?.unused_code_count || 0));
      setMfaRecoveryTotalCount(Number(payload?.total_code_count || 0));
      setMfaRecoveryPassword('');
      setMfaRecoveryPasswordOpen(false);
      setMfaMessage(result?.message || 'Recovery codes generated successfully.');
    } catch (err) {
      setMfaRecoveryPasswordError(err?.message || 'Unable to generate recovery codes.');
    } finally {
      setMfaRecoveryBusy(false);
    }
  }, [mfaRecoveryPassword]);

  const handleDownloadRecoveryCodes = useCallback(() => {
    if (!mfaRecoveryCodes.length) return;
    const lines = [
      'xSPECTRE recovery codes',
      'Each code works once. Store this file somewhere safe.',
      '',
      ...mfaRecoveryCodes,
    ];
    const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'xspectre-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [mfaRecoveryCodes]);

  const handleCopyMfaTotpSecret = useCallback(async () => {
    const text = String(mfaTotpSetup?.secret || '').trim();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setMfaError('');
      setMfaMessage('Secret copied to clipboard.');
      window.setTimeout(() => {
        setMfaMessage((msg) => (msg === 'Secret copied to clipboard.' ? '' : msg));
      }, 2000);
    } catch (_) {
      setMfaError('Unable to copy. Select the secret and copy manually.');
    }
  }, [mfaTotpSetup]);

  const refreshUsers = useCallback(() => {
    setUsersRefreshNonce((n) => n + 1);
    setUsersError(null);
    setUsersActionMessage('');
  }, []);

  const [manageUsersDeleteModal, setManageUsersDeleteModal] = useState(null);
  const [manageUsersEditModal, setManageUsersEditModal] = useState(null);

  // When the parent increments usersRefreshKey (e.g. after adding a user),
  // reset the loaded guard so the list re-fetches exactly once.
  useEffect(() => {
    if (usersRefreshKey === 0) return; // skip initial mount
    refreshUsers();
  // refreshUsers is stable (useCallback([])), usersRefreshKey is a primitive.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersRefreshKey]);

  const extractEntityId = (row) =>
    String(row?.uu_id || row?.id || row?.org_uuid || row?.site_uuid || row?.user_id || '').trim();

  const formatAdminActionError = useCallback((err, actionLabel = 'complete this action') => {
    const raw = String(err?.message || '').trim();
    const lower = raw.toLowerCase();

    if (!raw) return `Unable to ${actionLabel}.`;
    if (lower.includes('401') || lower.includes('unauthorized') || lower.includes('token')) {
      return `Session expired or unauthorized. Please log in again to ${actionLabel}.`;
    }
    if (lower.includes('403') || lower.includes('permission') || lower.includes('forbidden')) {
      return `You do not have permission to ${actionLabel}. Admin access is required.`;
    }
    if (lower.includes('uu_id') || lower.includes('missing id')) {
      return `Missing record identifier (uu_id). Please refresh data and retry.`;
    }
    if (lower.includes('organization') && (lower.includes('required') || lower.includes('invalid'))) {
      return `Organization value is missing or invalid. Select a valid organization and retry.`;
    }
    if (lower.includes('api unavailable') || lower.includes('networkerror') || lower.includes('failed to fetch')) {
      return 'Backend API is unavailable right now. Verify backend connectivity and try again.';
    }

    return raw;
  }, []);

  const getActionMessageTone = useCallback((message) => {
    const text = String(message || '').trim().toLowerCase();
    if (!text) return 'info';
    if (
      text.includes('failed') ||
      text.includes('error') ||
      text.includes('unable') ||
      text.includes('permission') ||
      text.includes('unauthorized') ||
      text.includes('forbidden')
    ) {
      return 'error';
    }
    if (
      text.includes('created ') ||
      text.includes('updated ') ||
      text.includes('deleted ') ||
      text.includes('saved') ||
      text.includes('success')
    ) {
      return 'success';
    }
    return 'info';
  }, []);

  useEffect(() => {
    if (!usersActionMessage) return undefined;
    if (getActionMessageTone(usersActionMessage) === 'error') return undefined;
    const id = window.setTimeout(() => setUsersActionMessage(''), 4500);
    return () => window.clearTimeout(id);
  }, [usersActionMessage, getActionMessageTone]);

  const handleUpdateUser = async (user) => {
    const userId = extractEntityId(user);
    if (!userId || usersActionLoading) return;
    if (isSelfManageUser(user)) {
      setUsersActionMessage('You cannot modify your own account from this table. Use Profile settings.');
      return;
    }
    setManageUserOrgSearch('');
    setManageUserSiteSearch('');
    setManageUsersEditModal({
      uu_id: userId,
      email: String(user?.email || '').trim(),
      name: String(user?.name || '').trim(),
      role: String(user?.role || 'Analyst').trim() || 'Analyst',
      timezone: String(user?.timezone || getPreferredTimezoneDefault()).trim() || getPreferredTimezoneDefault(),
      allOrganizationsAccess: Boolean(user?.all_organizations_access),
      allSitesAccess: Boolean(user?.all_sites_access),
      organizationAccessUuids: Array.isArray(user?.organization_access_uuids)
        ? user.organization_access_uuids.map((value) => String(value).trim().toLowerCase()).filter(Boolean)
        : [],
      siteAccessUuids: Array.isArray(user?.site_access_uuids)
        ? user.site_access_uuids.map((value) => String(value).trim().toLowerCase()).filter(Boolean)
        : [],
      resetMessage: '',
      error: '',
    });
  };

  const sendEditUserPasswordReset = async () => {
    const modal = manageUsersEditModal;
    const email = String(modal?.email || '').trim();
    if (!modal || !email || usersActionLoading) return;

    setUsersActionLoading(true);
    setManageUsersEditModal((current) => (current ? { ...current, error: '', resetMessage: '' } : current));
    try {
      await requestPasswordReset(email);
      setManageUsersEditModal((current) => (
        current ? { ...current, resetMessage: `Password reset link sent to ${email}.`, error: '' } : current
      ));
    } catch (err) {
      setManageUsersEditModal((current) => (
        current
          ? { ...current, error: err?.message || 'Unable to send the password reset link.', resetMessage: '' }
          : current
      ));
    } finally {
      setUsersActionLoading(false);
    }
  };

  const confirmEditUser = async () => {
    const modal = manageUsersEditModal;
    if (!modal) return;

    const userId = String(modal.uu_id || '').trim();
    const nextName = String(modal.name || '').trim();
    const nextRole = String(modal.role || '').trim();
    const nextTimezone = String(modal.timezone || '').trim() || getPreferredTimezoneDefault();

    if (!userId || usersActionLoading) return;
    if (!nextName) {
      setManageUsersEditModal((m) => ({ ...m, error: 'Name is required.' }));
      return;
    }
    if (!nextRole) {
      setManageUsersEditModal((m) => ({ ...m, error: 'Role is required.' }));
      return;
    }

    setUsersActionLoading(true);
    setUsersActionMessage('');
    try {
      await updateUserAdmin({
        uu_id: userId,
        id: userId,
        name: nextName,
        role: nextRole,
        timezone: nextTimezone,
        all_organizations_access: Boolean(modal.allOrganizationsAccess),
        all_sites_access: Boolean(modal.allSitesAccess),
        organization_access_uuids: modal.allOrganizationsAccess
          ? []
          : (Array.isArray(modal.organizationAccessUuids) ? modal.organizationAccessUuids : []),
        site_access_uuids: modal.allSitesAccess
          ? []
          : (Array.isArray(modal.siteAccessUuids) ? modal.siteAccessUuids : []),
      });
      setUsersActionMessage(`Updated user ${modal.email || userId}.`);
      setManageUsersEditModal(null);
      refreshUsers();
    } catch (err) {
      const message = formatAdminActionError(err, 'update user');
      setUsersActionMessage(message);
      setManageUsersEditModal((m) => ({ ...m, error: message }));
    } finally {
      setUsersActionLoading(false);
    }
  };

  const manageUsersEditTimezoneOptions = useMemo(
    () => getTimezoneOptions(manageUsersEditModal?.timezone || getPreferredTimezoneDefault()),
    [manageUsersEditModal?.timezone]
  );

  const handleDeleteUser = async (user) => {
    const userId = extractEntityId(user);
    if (!userId || usersActionLoading) return;
    if (isSelfManageUser(user)) {
      setUsersActionMessage('You cannot delete your own account from this table. Use Profile settings.');
      return;
    }
    setManageUsersDeleteModal({ users: [user], bulk: false, emailInput: '', error: '' });
  };

  const isManageUserInactive = useCallback((user) => {
    const raw = user?.is_active ?? user?.isActive ?? user?.active;
    if (typeof raw === 'boolean') return !raw;
    const safe = String(raw ?? '').trim().toLowerCase();
    if (!safe) return false;
    return safe === 'false' || safe === '0' || safe === 'inactive' || safe === 'pending';
  }, []);

  const closeManageUserRowActionsMenu = useCallback(() => {
    setOpenManageUserRowMenuKey(null);
    setManageUserRowMenuPlacement(null);
  }, []);

  useEffect(() => {
    if (!openManageUserRowMenuKey) return undefined;
    const onPointerDown = (event) => {
      const inMenu = manageUserRowActionsMenuRef.current?.contains(event.target);
      const inTrigger = manageUserRowActionsTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) closeManageUserRowActionsMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeManageUserRowActionsMenu();
    };
    const onRepositionClose = () => closeManageUserRowActionsMenu();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onRepositionClose);
    window.addEventListener('scroll', onRepositionClose, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onRepositionClose);
      window.removeEventListener('scroll', onRepositionClose, true);
    };
  }, [openManageUserRowMenuKey, closeManageUserRowActionsMenu]);

  const handleResendInvite = async (user) => {
    const userId = extractEntityId(user);
    if (!userId || usersActionLoading || !isManageUserInactive(user)) return;
    setUsersActionLoading(true);
    setUsersActionMessage('');
    try {
      await resendUserInvitation({ uu_id: userId, id: userId });
      setUsersActionMessage(`Invitation resent to ${user?.email || userId}.`);
      refreshUsers();
    } catch (err) {
      setUsersActionMessage(formatAdminActionError(err, 'resend invitation'));
    } finally {
      setUsersActionLoading(false);
    }
  };

  const confirmDeleteUsers = async () => {
    const modal = manageUsersDeleteModal;
    if (!modal) return;
    if (!modal.bulk) {
      const expected = String(modal.users[0]?.email || '').trim().toLowerCase();
      const entered = String(modal.emailInput || '').trim().toLowerCase();
      if (!entered) {
        setManageUsersDeleteModal((m) => ({ ...m, error: 'Please enter the email address to confirm.' }));
        return;
      }
      if (entered !== expected) {
        setManageUsersDeleteModal((m) => ({ ...m, error: 'Email does not match. Please re-enter.' }));
        return;
      }
    }
    setManageUsersDeleteModal((m) => ({ ...m, error: '' }));
    setUsersActionLoading(true);
    setUsersActionMessage('');
    try {
      const results = await Promise.allSettled(
        modal.users.map((u) => {
          const id = extractEntityId(u);
          return deleteUserAdmin({ uu_id: id, id });
        })
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      const deletedCount = modal.users.length - failedCount;
      if (failedCount === 0) {
        setUsersActionMessage(`Deleted ${deletedCount} user${deletedCount === 1 ? '' : 's'}.`);
      } else if (deletedCount === 0) {
        setUsersActionMessage('Failed to delete selected users.');
      } else {
        setUsersActionMessage(`Deleted ${deletedCount}. Failed to delete ${failedCount}.`);
      }
      const deletedSet = new Set(modal.users.map((u) => manageUserRowKey(u)));
      setSelectedManageUserKeys((prev) => prev.filter((k) => !deletedSet.has(k)));
      setManageUsersDeleteModal(null);
      refreshUsers();
    } catch (err) {
      setUsersActionMessage(formatAdminActionError(err, 'delete user'));
      setManageUsersDeleteModal(null);
    } finally {
      setUsersActionLoading(false);
    }
  };

  const handleBulkDeleteManageUsers = async () => {
    if (usersActionLoading || selectedManageUserKeys.length === 0) return;
    const selectedKeySet = new Set(selectedManageUserKeys);
    const selectedRows = usersList.filter((u) => selectedKeySet.has(manageUserRowKey(u)));
    const deletableRows = selectedRows.filter((u) => !isSelfManageUser(u));
    const selectedWithIds = deletableRows
      .map((u) => ({ row: u, id: extractEntityId(u) }))
      .filter((entry) => Boolean(entry.id));
    const selectedCount = selectedWithIds.length;

    if (selectedCount === 0) {
      setUsersActionMessage('No valid users selected for deletion (current user cannot be deleted).');
      return;
    }

    setManageUsersDeleteModal({ users: deletableRows, bulk: true, emailInput: '', error: '' });
  };

  return (
    <div className="profile-settings" data-tour="profile-settings-page">
      {!hideProfileChrome && (
        <div className="profile-settings-header-row">
          <h1 className="overview-title">Profile Settings</h1>
        </div>
      )}

      {!hideProfileChrome && (
        <div
          className="scans-network-scope-tabs profile-scope-tabs"
          data-tour="profile-settings-tabs"
          role="tablist"
          aria-label="Profile settings tabs"
        >
          <div className="scans-network-scope-tabs-track profile-scope-tabs-track">
            <button
              type="button"
              role="tab"
              id="profile-tab-general"
              aria-selected={activeTab === 'personal_info'}
              aria-controls="profile-tab-panel-general"
              className={`scans-network-scope-tab${activeTab === 'personal_info' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('personal_info')}
            >
              <span className="scans-network-scope-tab-main">
                <span className="scans-network-scope-tab-icon" aria-hidden="true">
                  {PROFILE_TAB_ICONS.general}
                </span>
                <span className="scans-network-scope-tab-label">General</span>
              </span>
            </button>
            <button
              type="button"
              role="tab"
              id="profile-tab-account-security"
              aria-selected={activeTab === 'account_security'}
              aria-controls="profile-tab-panel-account-security"
              className={`scans-network-scope-tab${activeTab === 'account_security' ? ' is-active' : ''}`}
              data-tour="profile-settings-tab-security"
              onClick={() => setActiveTab('account_security')}
            >
              <span className="scans-network-scope-tab-main">
                <span className="scans-network-scope-tab-icon" aria-hidden="true">
                  {PROFILE_TAB_ICONS.security}
                </span>
                <span className="scans-network-scope-tab-label">Security &amp; Access</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {activeTab === 'personal_info' && (
        <div
          className="profile-settings-general-panel"
          id="profile-tab-panel-general"
          role="tabpanel"
          aria-labelledby="profile-tab-general"
        >
          <div className="profile-general">
            <form
              id="profile-settings-form"
              className="profile-general-form"
              onSubmit={handleSubmit}
            >
              <div
                className={`profile-general-row profile-general-card${isEditingProfile ? ' is-editing' : ' is-locked'}`}
                data-tour="profile-settings-account"
              >
                <div className="profile-general-meta">
                  <h3 className="profile-general-title">Account details</h3>
                  <p className="profile-general-desc">
                    Legal name, email, and the time zone used for schedules and timestamps.
                  </p>
                </div>
                <div className="profile-general-controls profile-general-controls--split">
                  <div className="profile-settings-field" data-tour="profile-settings-name">
                    <label htmlFor="profile-first-name">First name</label>
                    <input
                      id="profile-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      autoComplete="given-name"
                      className="profile-settings-input"
                      readOnly={!isEditingProfile}
                      disabled={!isEditingProfile}
                    />
                  </div>
                  <div className="profile-settings-field">
                    <label htmlFor="profile-last-name">Last name</label>
                    <input
                      id="profile-last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      autoComplete="family-name"
                      className="profile-settings-input"
                      readOnly={!isEditingProfile}
                      disabled={!isEditingProfile}
                    />
                  </div>
                  <div className="profile-settings-field" data-tour="profile-settings-contact">
                    <label htmlFor="profile-email">Email</label>
                    <div className="profile-settings-input-wrap">
                      <IconEnvelope />
                      <input
                        id="profile-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        autoComplete="email"
                        className="profile-settings-input profile-settings-input-with-icon"
                        readOnly={!isEditingProfile}
                        disabled={!isEditingProfile}
                      />
                    </div>
                  </div>
                  <div className="profile-settings-field">
                    <label htmlFor="profile-timezone">Time zone</label>
                    <select
                      id="profile-timezone"
                      value={profileTimezone}
                      onChange={(e) => setProfileTimezone(e.target.value)}
                      className="profile-settings-input"
                      disabled={!isEditingProfile}
                    >
                      {profileTimezoneOptions.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="profile-general-actions">
                    {submitSuccessMessage === 'Profile updated successfully.' ? (
                      <p className="profile-general-save-status" role="status">
                        Profile updated.
                      </p>
                    ) : null}
                    {!isEditingProfile ? (
                      <button
                        type="button"
                        className="profile-settings-edit-btn"
                        onClick={() => {
                          setSubmitError(null);
                          setSubmitSuccessMessage('');
                          setIsEditingProfile(true);
                        }}
                      >
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="profile-settings-cancel-btn"
                          onClick={handleCancelEdit}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`profile-settings-submit profile-general-password-submit${
                            isProfileDirty ? ' is-ready' : ''
                          }`}
                          disabled={loading || !isProfileDirty}
                        >
                          Save Changes
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>

            <form
              className="profile-general-form profile-general-password-form"
              onSubmit={handlePasswordSubmit}
              data-tour="profile-settings-password"
            >
              <div
                className={`profile-general-row profile-general-row--last profile-general-card${
                  isEditingPassword ? ' is-editing' : ' is-locked'
                }`}
              >
                <div className="profile-general-meta">
                  <h3 className="profile-general-title">Password</h3>
                  <p className="profile-general-desc">
                    Update your sign-in password. You will be signed out after a successful change.
                  </p>
                </div>
                <div className="profile-general-controls">
                  <div className="profile-settings-field">
                    <label htmlFor="profile-current-password">
                      Current password
                      {isEditingPassword && newPassword ? (
                        <span className="profile-settings-required-mark" aria-hidden="true"> *</span>
                      ) : null}
                    </label>
                    <div className="profile-settings-input-wrap">
                      <IconLock />
                      <input
                        id="profile-current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={isEditingPassword ? 'Current password' : '••••••••'}
                        autoComplete="current-password"
                        className="profile-settings-input profile-settings-input-with-icon"
                        required={Boolean(isEditingPassword && newPassword)}
                        readOnly={!isEditingPassword}
                        disabled={!isEditingPassword}
                      />
                      {isEditingPassword ? (
                        <button
                          type="button"
                          className="profile-settings-toggle-password"
                          onClick={() => setShowCurrentPassword((p) => !p)}
                          aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                        >
                          {showCurrentPassword ? <IconEyeOff /> : <IconEye />}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="profile-settings-field">
                    <label htmlFor="profile-new-password">New password</label>
                    <div className="profile-settings-input-wrap">
                      <IconLock />
                      <input
                        id="profile-new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={isEditingPassword ? 'At least 8 characters' : '••••••••'}
                        autoComplete="new-password"
                        className="profile-settings-input profile-settings-input-with-icon"
                        readOnly={!isEditingPassword}
                        disabled={!isEditingPassword}
                      />
                      {isEditingPassword ? (
                        <button
                          type="button"
                          className="profile-settings-toggle-password"
                          onClick={() => setShowNewPassword((p) => !p)}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          {showNewPassword ? <IconEyeOff /> : <IconEye />}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="profile-settings-field">
                    <label htmlFor="profile-password-confirm">Confirm new password</label>
                    <div className="profile-settings-input-wrap">
                      <IconLock />
                      <input
                        id="profile-password-confirm"
                        type="password"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        placeholder={isEditingPassword ? 'Confirm new password' : '••••••••'}
                        autoComplete="new-password"
                        className="profile-settings-input profile-settings-input-with-icon"
                        readOnly={!isEditingPassword}
                        disabled={!isEditingPassword}
                      />
                    </div>
                  </div>
                  <div className="profile-general-actions">
                    {!isEditingPassword ? (
                      <button
                        type="button"
                        className="profile-settings-edit-btn"
                        onClick={() => {
                          setSubmitError(null);
                          setSubmitSuccessMessage('');
                          setIsEditingPassword(true);
                        }}
                      >
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="profile-settings-cancel-btn"
                          onClick={handleClearPassword}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`profile-settings-submit profile-general-password-submit${
                            isPasswordFormFilled ? ' is-ready' : ''
                          }`}
                          disabled={loading || !isPasswordFormFilled}
                        >
                          Update password
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>

          {submitError ? (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {submitError}
            </div>
          ) : null}
          {submitSuccessMessage && submitSuccessMessage !== 'Profile updated successfully.' ? (
            <div className="profile-settings-message profile-settings-success">
              {submitSuccessMessage}
            </div>
          ) : null}
        </div>
      )}
      {activeTab === 'account_security' && (
        <div className="profile-settings-account-security-panel" id="profile-tab-panel-account-security" role="tabpanel" aria-labelledby="profile-tab-account-security">
          <div className="profile-settings-form profile-settings-security-form">
          <section className="profile-settings-section" data-tour="profile-settings-mfa">
            <div className="profile-settings-section-title-row">
              <h3 className="profile-settings-section-title">Multi-factor authentication</h3>
            </div>
            <div className="profile-settings-section-body">
              <div className="profile-settings-mfa-stack">
                <div className="profile-settings-field profile-settings-mfa-method-group">
                  <div
                    className="profile-settings-mfa-method-list"
                    role="tablist"
                    aria-label="MFA method"
                  >
                    <button
                      type="button"
                      className={`profile-settings-mfa-method-row ${mfaMethod === 'email' ? 'is-active' : ''}`}
                      onClick={() => { setMfaMethod('email'); setMfaError(''); setMfaMessage(''); }}
                      disabled={mfaBusy}
                      aria-pressed={mfaMethod === 'email'}
                      aria-label="Email MFA"
                    >
                      {renderMfaEnrolledBadge('email')}
                      <span className="profile-settings-mfa-method-row-icon" aria-hidden="true"><IconEnvelope /></span>
                      <span className="profile-settings-mfa-method-row-body">
                        <span className="profile-settings-mfa-tab-label">Email</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`profile-settings-mfa-method-row ${mfaMethod === 'sms' ? 'is-active' : ''}`}
                      onClick={() => { setMfaMethod('sms'); setMfaError(''); setMfaMessage(''); }}
                      disabled={mfaBusy}
                      aria-pressed={mfaMethod === 'sms'}
                      aria-label="Text message MFA"
                    >
                      {renderMfaEnrolledBadge('sms')}
                      <span className="profile-settings-mfa-method-row-icon" aria-hidden="true"><IconSmartphone /></span>
                      <span className="profile-settings-mfa-method-row-body">
                        <span className="profile-settings-mfa-tab-label">Text message</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`profile-settings-mfa-method-row ${mfaMethod === 'app' ? 'is-active' : ''}`}
                      onClick={() => { setMfaMethod('app'); setMfaError(''); setMfaMessage(''); }}
                      disabled={isMfaMethodBlocked('app') || mfaBusy}
                      aria-pressed={mfaMethod === 'app'}
                      aria-label="Authenticator app MFA"
                    >
                      {renderMfaEnrolledBadge('app')}
                      <span className="profile-settings-mfa-method-row-icon" aria-hidden="true"><IconAuthenticator /></span>
                      <span className="profile-settings-mfa-method-row-body">
                        <span className="profile-settings-mfa-tab-label">Authenticator</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`profile-settings-mfa-method-row ${mfaMethod === 'recovery' ? 'is-active' : ''}`}
                      onClick={() => { setMfaMethod('recovery'); setMfaError(''); setMfaMessage(''); }}
                      disabled={isMfaMethodBlocked('recovery') || mfaBusy}
                      aria-pressed={mfaMethod === 'recovery'}
                      aria-label="Recovery codes MFA"
                    >
                      {renderMfaEnrolledBadge('recovery')}
                      <span className="profile-settings-mfa-method-row-icon" aria-hidden="true"><IconRecoveryCodes /></span>
                      <span className="profile-settings-mfa-method-row-body">
                        <span className="profile-settings-mfa-tab-label">Recovery codes</span>
                      </span>
                    </button>
                  </div>

                  {mfaActiveMethod ? (
                    <div className="profile-settings-mfa-help profile-settings-mfa-disable-card">
                      <p className="profile-settings-section-desc profile-settings-mfa-disable-copy">
                        Turn off the active method to sign in with password only. You can enable a different method anytime.
                      </p>
                      <div className="profile-settings-mfa-disable-actions">
                        <button
                          type="button"
                          className="profile-settings-mfa-disable-btn"
                          onClick={openDisablePasswordPrompt}
                          disabled={mfaBusy || mfaDisableBusy}
                        >
                          Disable Multi-factor authentication
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {mfaMethod === 'email' && (
                    <div className="profile-settings-mfa-email-panel profile-settings-mfa-detail-card">
                      {normalizeMfaMethod(mfaActiveMethod) === 'email' ? (
                        <p className="profile-settings-mfa-method-active-copy" role="status">
                          Email MFA is active. Verification codes are sent to{' '}
                          <strong>
                            {String(savedSnapshot.email || userProfile?.email || email || '').trim() || 'your email'}
                          </strong>
                          . Use Disable Multi-factor authentication above to turn it off.
                        </p>
                      ) : isMfaMethodBlocked('email') ? (
                        <p className="profile-settings-mfa-method-active-copy" role="status">
                          Disable {mfaMethodDisplayLabel(mfaActiveMethod)} MFA before enabling Email.
                        </p>
                      ) : (
                        <>
                          <p className="profile-settings-mfa-readonly-footnote">
                            To use a different address, go to the General tab and update Contact &amp; region.
                          </p>
                          <div className="profile-settings-mfa-email-panel-main">
                            <div className="profile-settings-mfa-email-panel-row">
                              <span className="profile-settings-mfa-email-panel-icon"><IconEnvelope /></span>
                              <div className="profile-settings-mfa-email-panel-body">
                                <span className="profile-settings-mfa-email-panel-label">Verification codes will be sent to</span>
                                <span className="profile-settings-mfa-email-panel-value">
                                  {String(savedSnapshot.email || userProfile?.email || email || '').trim() || ''}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="profile-settings-submit profile-settings-mfa-inline-send"
                              onClick={handleMfaSendCode}
                              disabled={mfaBusy || mfaSendCooldownSeconds > 0}
                            >
                              {mfaSendCooldownSeconds > 0
                                ? `Send verification code (${mfaSendCooldownSeconds}s)`
                                : 'Send verification code'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {mfaMethod === 'sms' && (
                    <div className="profile-settings-field profile-settings-mfa-phone-field profile-settings-mfa-phone-in-group profile-settings-mfa-detail-card">
                      {normalizeMfaMethod(mfaActiveMethod) === 'sms' ? (
                        <p className="profile-settings-mfa-method-active-copy" role="status">
                          Text message MFA is active
                          {mfaSmsE164Number ? (
                            <>
                              {' '}for <strong>{mfaSmsE164Number}</strong>
                            </>
                          ) : null}
                          . Use Disable Multi-factor authentication above to turn it off or change number.
                        </p>
                      ) : isMfaMethodBlocked('sms') ? (
                        <p className="profile-settings-mfa-method-active-copy" role="status">
                          Disable {mfaMethodDisplayLabel(mfaActiveMethod)} MFA before enabling Text message.
                        </p>
                      ) : (
                        <>
                          <p id="profile-mfa-sms-inline-hint" className="profile-settings-mfa-sms-inline-hint">
                            Select your country code, then enter your mobile number without the country prefix.
                          </p>
                          <div className="profile-settings-mfa-sms-inline-row">
                            <div className="profile-settings-mfa-sms-dial" ref={mfaSmsDialRef}>
                              <button
                                type="button"
                                id="profile-mfa-sms-dial"
                                className={`profile-settings-input profile-settings-mfa-sms-dial-select${
                                  mfaSmsDialOpen ? ' is-open' : ''
                                }`}
                                disabled={mfaBusy}
                                aria-label="Country calling code"
                                aria-describedby="profile-mfa-sms-inline-hint"
                                aria-haspopup="listbox"
                                aria-expanded={mfaSmsDialOpen}
                                aria-controls="profile-mfa-sms-dial-list"
                                onClick={() => setMfaSmsDialOpen((open) => !open)}
                              >
                                {MFA_SMS_COUNTRY_DIAL_OPTIONS.find((opt) => opt.dial === mfaSmsDialCode)?.label
                                  || mfaSmsDialCode}
                              </button>
                              {mfaSmsDialOpen ? (
                                <ul
                                  id="profile-mfa-sms-dial-list"
                                  className="profile-settings-mfa-sms-dial-menu"
                                  role="listbox"
                                  aria-label="Country calling code"
                                >
                                  {MFA_SMS_COUNTRY_DIAL_OPTIONS.map((opt) => (
                                    <li key={opt.dial} role="presentation">
                                      <button
                                        type="button"
                                        role="option"
                                        aria-selected={mfaSmsDialCode === opt.dial}
                                        className={`profile-settings-mfa-sms-dial-option${
                                          mfaSmsDialCode === opt.dial ? ' is-selected' : ''
                                        }`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          setMfaSmsDialCode(opt.dial);
                                          setMfaSmsDialOpen(false);
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                            <input
                              id="profile-mfa-phone-national"
                              type="tel"
                              inputMode="tel"
                              autoComplete="tel-national"
                              value={mfaSmsNationalNumber}
                              onChange={(e) => setMfaSmsNationalNumber(e.target.value)}
                              placeholder="412 345 678"
                              className="profile-settings-input profile-settings-mfa-sms-national-input"
                              disabled={mfaBusy}
                              aria-label="Mobile number"
                              aria-describedby="profile-mfa-sms-inline-hint"
                            />
                            <button
                              type="button"
                              className="profile-settings-submit profile-settings-mfa-inline-send"
                              onClick={handleMfaSendCode}
                              disabled={mfaBusy || mfaSendCooldownSeconds > 0}
                            >
                              {mfaSendCooldownSeconds > 0
                                ? `Send verification code (${mfaSendCooldownSeconds}s)`
                                : 'Send verification code'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {mfaMethod === 'app' && mfaTotpLoading ? (
                    <p className="profile-settings-section-desc profile-settings-mfa-app-loading" role="status">
                      Preparing authenticator setup…
                    </p>
                  ) : null}

                  {mfaMethod === 'app' && !mfaTotpLoading && mfaTotpSetup ? (
                    <div className="profile-settings-mfa-app-card profile-settings-mfa-phone-in-group profile-settings-mfa-detail-card">
                      <div className="profile-settings-mfa-app-body">
                        <p id="profile-mfa-app-qr-hint" className="profile-settings-mfa-app-qr-hint">
                          Scan the QR with your authenticator app, then enter the verification code below.
                        </p>
                        <div className="profile-settings-mfa-app-main-row">
                          <div className="profile-settings-mfa-app-qr-column">
                            <div className="profile-settings-mfa-app-qr-wrap">
                              {mfaTotpSetup.totpSvgDataUri ? (
                                <img
                                  src={mfaTotpSetup.totpSvgDataUri}
                                  alt="Scan this QR code with your authenticator app"
                                  width={148}
                                  height={148}
                                  className="profile-settings-mfa-app-qr-svg"
                                />
                              ) : (
                                <MfaTotpQrPlaceholder size={148} secret={mfaTotpSetup.secret} />
                              )}
                            </div>
                          </div>
                          <div className="profile-settings-mfa-app-fields">
                          <div className="profile-settings-mfa-app-field-block">
                            <label htmlFor="profile-mfa-app-secret" className="profile-settings-mfa-app-secret-label">
                              Authenticator secret key
                            </label>
                            <div className="profile-settings-mfa-app-secret-wrap">
                              <input
                                id="profile-mfa-app-secret"
                                type="text"
                                readOnly
                                tabIndex={0}
                                value={mfaTotpSetup.secret}
                                className="profile-settings-mfa-app-secret-value"
                                aria-label="Authenticator secret key"
                              />
                              <button
                                type="button"
                                className="profile-settings-mfa-app-secret-copy"
                                onClick={handleCopyMfaTotpSecret}
                                aria-label="Copy authenticator secret to clipboard"
                                title="Copy to clipboard"
                                disabled={mfaBusy}
                              >
                                <IconCopy />
                              </button>
                            </div>
                          </div>
                          <div className="profile-settings-mfa-app-code-row">
                            <label htmlFor="profile-mfa-app-code" className="profile-settings-mfa-app-code-label">
                              Authenticator code:
                            </label>
                            <div className="profile-settings-mfa-app-code-inline">
                              <input
                                id="profile-mfa-app-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={mfaAppTotpCode}
                                onChange={(e) => setMfaAppTotpCode(e.target.value)}
                                placeholder="Code"
                                className="profile-settings-input profile-settings-mfa-app-code-input"
                                disabled={mfaBusy}
                              />
                              <button
                                type="button"
                                className="profile-settings-submit profile-settings-mfa-inline-send"
                                onClick={handleMfaAppActivate}
                                disabled={mfaBusy}
                              >
                                Activate
                              </button>
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {mfaMethod === 'recovery' && (
                    <div className="profile-settings-mfa-detail-card profile-settings-mfa-phone-in-group">
                      <h4 className="profile-settings-mfa-detail-title">Recovery codes</h4>
                      <p className="profile-settings-mfa-detail-lead">
                        Always active alongside your primary MFA method. Use a code to sign in if you lose access to your
                        authenticator. Each code works once  store them somewhere safe.
                      </p>
                      {mfaRecoveryLoading ? (
                        <p className="profile-settings-section-desc" role="status">
                          Loading recovery codes…
                        </p>
                      ) : null}
                      {!mfaRecoveryLoading && mfaRecoveryCodes.length > 0 ? (
                        <ul className="profile-settings-mfa-detail-bullets">
                          {mfaRecoveryCodes.map((code) => (
                            <li key={code}><code>{code}</code></li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="profile-settings-mfa-recovery-actions">
                        {mfaRecoveryCodes.length > 0 ? (
                          <button
                            type="button"
                            className="profile-settings-cancel-btn profile-settings-mfa-recovery-download"
                            onClick={openRecoveryPasswordPrompt}
                            disabled={mfaBusy || mfaRecoveryBusy}
                          >
                            <IconRegenerate />
                            Regenerate
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="profile-settings-submit profile-settings-mfa-inline-send"
                          onClick={mfaRecoveryCodes.length > 0 ? handleDownloadRecoveryCodes : openRecoveryPasswordPrompt}
                          disabled={mfaBusy || mfaRecoveryBusy}
                        >
                          {mfaRecoveryCodes.length > 0 ? (
                            <>
                              <IconDownload />
                              Download
                            </>
                          ) : (
                            <>
                              <IconRegenerate />
                              Regenerate
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {mfaError && !(mfaChallengeId && mfaChallengeMethod) ? (
                  <div className="profile-settings-message profile-settings-error" role="alert">
                    {mfaError}
                  </div>
                ) : null}
                {mfaMessage && !(mfaChallengeId && mfaChallengeMethod) ? (
                  <div className="profile-settings-message profile-settings-success" role="status">
                    {mfaMessage}
                  </div>
                ) : null}
                {!mfaBusy && mfaSendCooldownSeconds > 0 && isMfaOtpUiMethod(mfaMethod) ? (
                  <div className="profile-settings-message profile-settings-info" role="status">
                    You can request another verification code in {mfaSendCooldownSeconds}s.
                  </div>
                ) : null}
              </div>
            </div>
          </section>
          </div>
        </div>
      )}
      {activeTab === 'manage_users' && (
        <div className="manage-users-panel" data-tour="settings-users-page">
          {(usersLoading || orgsLoading) && !usersError ? (
            <PageDataLoader label="Loading users" />
          ) : (
            <>
          {usersError && (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {String(usersError || '').toLowerCase().includes('api unavailable')
                ? 'Backend not connected yet. Connect the backend to load users.'
                : usersError}
            </div>
          )}
          {orgsError && !usersError && (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {orgsError}
            </div>
          )}

          {!usersLoading && !usersError && (
            <div className="manage-users-list">
              <div className="manage-users-filters-row">
                <div className="risks-page-filters">
                  {usersHeaderActionsHost
                    ? createPortal(
                        <button
                          type="button"
                          data-tour="settings-users-add"
                          className="users-orgs-users-action-btn users-orgs-users-action-btn--primary users-orgs-users-action-btn--compact users-orgs-top-header-manage-btn page-header-cta"
                          onClick={() => {
                            if (typeof onAddInviteUser === 'function') onAddInviteUser();
                          }}
                        >
                          <svg className="page-header-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Add or invite user
                        </button>,
                        usersHeaderActionsHost
                      )
                    : null}
                  <div className="users-orgs-users-company-filter users-orgs-role-filter-field" ref={usersFilterRoleRef}>
                    <div className="users-orgs-users-company-filter-bar">
                      <button
                        type="button"
                        className={`users-orgs-users-company-filter-main${
                          !usersFilterRole ? ' users-orgs-users-company-filter-main--all-orgs' : ''
                        }`}
                        aria-expanded={usersFilterRoleOpen}
                        aria-haspopup="listbox"
                        onClick={() => setUsersFilterRoleOpen((open) => !open)}
                      >
                        <svg
                          className="users-orgs-users-company-filter-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                        <span className="users-orgs-users-company-filter-label">
                          {usersFilterRole || 'All Roles'}
                        </span>
                        <span className="users-orgs-users-company-filter-chevron" aria-hidden="true">▾</span>
                      </button>
                      {usersFilterRole ? (
                        <button
                          type="button"
                          className="users-orgs-users-company-filter-clear"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setUsersFilterRole('');
                            setUsersFilterRoleOpen(false);
                          }}
                          aria-label="Clear role filter"
                          title="Clear role filter"
                        >
                          <svg
                            width={14}
                            height={14}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                    {usersFilterRoleOpen ? (
                      <div className="users-orgs-users-company-filter-panel" role="presentation">
                        <ul className="users-orgs-users-company-filter-list" role="listbox">
                          <li role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={!usersFilterRole}
                              className={`users-orgs-users-company-filter-option${!usersFilterRole ? ' is-selected' : ''}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setUsersFilterRole('');
                                setUsersFilterRoleOpen(false);
                              }}
                            >
                              All roles
                            </button>
                          </li>
                          {usersRoleFilterOptions.map((roleName) => (
                            <li key={roleName} role="presentation">
                              <button
                                type="button"
                                role="option"
                                aria-selected={usersFilterRole === roleName}
                                className={`users-orgs-users-company-filter-option${usersFilterRole === roleName ? ' is-selected' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setUsersFilterRole(roleName);
                                  setUsersFilterRoleOpen(false);
                                }}
                              >
                                {roleName}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                  {selectedManageUserKeys.length > 0 ? (
                    <button
                      type="button"
                      className="assets-bulk-delete-btn manage-users-bulk-delete-btn"
                      onClick={handleBulkDeleteManageUsers}
                      disabled={usersActionLoading}
                      title={`Delete ${selectedManageUserKeys.length} selected user${selectedManageUserKeys.length === 1 ? '' : 's'}`}
                    >
                      Delete ({selectedManageUserKeys.length})
                    </button>
                  ) : null}
                  <div className="subscription-org-management-search manage-users-inline-search">
                    <label htmlFor="manage-users-filter-search" className="edi-sr-only">
                      Search users
                    </label>
                    <div className="subscription-org-management-search-inner">
                      <input
                        id="manage-users-filter-search"
                        type="search"
                        className="subscription-org-management-search-input"
                        placeholder="Search users"
                        value={usersFilterSearchQuery}
                        onChange={(e) => setUsersFilterSearchQuery(sanitizeUniversalSearchInput(e.target.value))}
                        autoComplete="off"
                        spellCheck={false}
                        maxLength={UNIVERSAL_SEARCH_MAX_LEN}
                      />
                      {usersFilterSearchQuery ? (
                        <button
                          type="button"
                          className="ghost-exposure-sys-table-search-clear"
                          onClick={() => setUsersFilterSearchQuery('')}
                          aria-label="Clear search"
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="assets-table-shell assets-table-shell--inventory-body manage-users-table-shell" data-tour="settings-users-table">
                <div
                  className="risks-page-table-header-frame"
                  style={{ '--risks-header-gutter': `${manageUsersHeaderGutterPx}px` }}
                >
                  <div ref={manageUsersTableHeaderRef} className="risks-page-table-header">
                    <table className={`${manageUsersTableClassName} risks-page-table--header`} style={manageUsersTableStyle}>
                      <ResizableColgroup columns={manageUsersTableColumns} widths={manageUsersColumnWidths} />
                      <ResizableTableHead
                        columns={manageUsersTableColumns}
                        widths={manageUsersColumnWidths}
                        onResizeStart={onManageUsersColumnResizeStart}
                        resizingKey={manageUsersColumnResizingKey}
                        sortConfig={manageUsersSortConfig}
                        onSort={handleManageUsersSort}
                        getSortIcon={getManageUsersSortIcon}
                        resizeHandleClassName="risks-page-col-resize-handle"
                      />
                    </table>
                  </div>
                  <div className="risks-page-table-header-gutter" aria-hidden="true" />
                </div>
                <div
                  ref={manageUsersTableScrollRef}
                  className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
                  onScroll={handleManageUsersTableScroll}
                >
                  <table className={`${manageUsersTableClassName} risks-page-table--body`} style={manageUsersTableStyle}>
                    <ResizableColgroup columns={manageUsersTableColumns} widths={manageUsersColumnWidths} />
                    <tbody>
                      {sortedFilteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="assets-empty-cell">
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        visibleManageUserRows.map((u) => {
                          const isSelf = isSelfManageUser(u);
                          const isInactiveUser = isManageUserInactive(u);
                          return (
                            <tr key={u.uu_id || u.email}>
                              <td className="risks-page-td-checkbox assets-cell-center users-orgs-settings-td-checkbox">
                                <input
                                  type="checkbox"
                                  className="risks-page-checkbox users-orgs-settings-checkbox"
                                  checked={!isSelf && selectedManageUserKeys.includes(manageUserRowKey(u))}
                                  disabled={isSelf}
                                  onChange={() => toggleManageUserRowSelected(u)}
                                  aria-label={`Select user ${u.name || u.email || ''}`}
                                />
                              </td>
                              <td>{u.name || u.email || ''}</td>
                              <td>{u.email || ''}</td>
                              <td>{u.role || ''}</td>
                              <td>{u.organization || ''}</td>
                              <td className="manage-users-td-actions">
                                <div className="users-orgs-actions-cell manage-users-actions-cell">
                                  {!isSelf ? (
                                    <button
                                      type="button"
                                      className="connectors-settings-row-action connectors-settings-row-action--danger connectors-settings-row-action--icon-only"
                                      onClick={() => {
                                        closeManageUserRowActionsMenu();
                                        handleDeleteUser(u);
                                      }}
                                      disabled={usersActionLoading}
                                      aria-label={`Delete user ${u.name || u.email || ''}`}
                                      title="Delete user"
                                    >
                                      <TrashIcon className="connectors-settings-delete-icon" size={14} strokeWidth={2.35} />
                                    </button>
                                  ) : null}
                                  <div
                                    className="assets-row-actions-menu-wrap"
                                    ref={
                                      openManageUserRowMenuKey === manageUserRowKey(u)
                                        ? manageUserRowActionsTriggerRef
                                        : null
                                    }
                                  >
                                    <button
                                      type="button"
                                      className={[
                                        'assets-more-btn',
                                        openManageUserRowMenuKey === manageUserRowKey(u)
                                          ? 'assets-more-btn--open'
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      title="More actions"
                                      aria-label={`More actions for ${u.name || u.email || 'user'}`}
                                      aria-haspopup="menu"
                                      aria-expanded={openManageUserRowMenuKey === manageUserRowKey(u)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const menuKey = manageUserRowKey(u);
                                        if (openManageUserRowMenuKey === menuKey) {
                                          closeManageUserRowActionsMenu();
                                          return;
                                        }
                                        const trigger = e.currentTarget;
                                        if (!trigger) return;
                                        const rect = trigger.getBoundingClientRect();
                                        const estimatedMenuHeight = isSelf ? 52 : 88;
                                        const openUp =
                                          window.innerHeight - rect.bottom < estimatedMenuHeight + 16;
                                        const isLightTheme = Boolean(
                                          document.querySelector('.dashboard-container.theme-light')
                                        );
                                        setManageUserRowMenuPlacement({
                                          top: openUp ? rect.top - 4 : rect.bottom + 4,
                                          right: Math.max(8, window.innerWidth - rect.right),
                                          openUp,
                                          isLightTheme,
                                        });
                                        setOpenManageUserRowMenuKey(menuKey);
                                      }}
                                    >
                                      <svg
                                        className="assets-more-btn-icon"
                                        viewBox="0 0 20 20"
                                        width="14"
                                        height="14"
                                        fill="currentColor"
                                        aria-hidden="true"
                                      >
                                        <circle cx="4" cy="10" r="1.6" />
                                        <circle cx="10" cy="10" r="1.6" />
                                        <circle cx="16" cy="10" r="1.6" />
                                      </svg>
                                    </button>
                                    {openManageUserRowMenuKey === manageUserRowKey(u) &&
                                    manageUserRowMenuPlacement
                                      ? createPortal(
                                          <div
                                            ref={manageUserRowActionsMenuRef}
                                            className={[
                                              'assets-row-actions-menu',
                                              'assets-row-actions-menu--portal',
                                              manageUserRowMenuPlacement.openUp
                                                ? 'assets-row-actions-menu--up'
                                                : null,
                                              manageUserRowMenuPlacement.isLightTheme
                                                ? 'assets-row-actions-menu--light'
                                                : null,
                                            ]
                                              .filter(Boolean)
                                              .join(' ')}
                                            role="menu"
                                            style={{
                                              top: manageUserRowMenuPlacement.top,
                                              right: manageUserRowMenuPlacement.right,
                                            }}
                                          >
                                            {isSelf ? (
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className="assets-row-actions-menu-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  closeManageUserRowActionsMenu();
                                                  goToProfileSettings();
                                                }}
                                              >
                                                Profile
                                              </button>
                                            ) : (
                                              <>
                                                <button
                                                  type="button"
                                                  role="menuitem"
                                                  className="assets-row-actions-menu-item"
                                                  disabled={usersActionLoading}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    closeManageUserRowActionsMenu();
                                                    handleUpdateUser(u);
                                                  }}
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  role="menuitem"
                                                  className="assets-row-actions-menu-item"
                                                  disabled={!isInactiveUser || usersActionLoading}
                                                  title={
                                                    isInactiveUser
                                                      ? 'Resend invitation'
                                                      : 'Only pending invitations can be resent'
                                                  }
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    closeManageUserRowActionsMenu();
                                                    handleResendInvite(u);
                                                  }}
                                                >
                                                  Resend
                                                </button>
                                              </>
                                            )}
                                          </div>,
                                          document.body
                                        )
                                      : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <TablePagination {...manageUsersPagination} itemLabel="users" />
            </div>
          )}
            </>
          )}
        </div>
      )}
      {loadError && (
        <div className="profile-settings-message profile-settings-error profile-settings-error-bottom" role="alert">
          {loadError}
        </div>
      )}

      {manageUsersEditModal ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setManageUsersEditModal(null);
          }}
        >
          <div
            className="assets-detail-modal users-orgs-user-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-modal-title"
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header">
                <div className="assets-detail-modal-title" id="edit-user-modal-title">
                  Edit User
                </div>
                <div className="assets-detail-modal-actions">
                  <button
                    type="button"
                    className="assets-detail-modal-close"
                    aria-label="Close"
                    onClick={() => setManageUsersEditModal(null)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form
                className="users-orgs-user-detail-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  confirmEditUser();
                }}
              >
                <div className="users-orgs-user-detail-form-scroll">
                <section className="users-orgs-add-invite-card" aria-label="User details">
                  <div className="users-orgs-add-invite-card-body">
                    <div className="users-orgs-user-detail-field">
                      <label htmlFor="manage-users-edit-name">Name</label>
                      <input
                        id="manage-users-edit-name"
                        type="text"
                        className="users-orgs-user-detail-input"
                        value={manageUsersEditModal.name}
                        onChange={(e) => setManageUsersEditModal((m) => ({ ...m, name: e.target.value, error: '' }))}
                        autoComplete="off"
                      />
                    </div>
                    <div className="users-orgs-user-detail-field">
                      <label htmlFor="manage-users-edit-timezone">Time zone</label>
                      <select
                        id="manage-users-edit-timezone"
                        className="users-orgs-user-detail-input users-orgs-user-detail-select"
                        value={manageUsersEditModal.timezone}
                        onChange={(e) => setManageUsersEditModal((m) => ({ ...m, timezone: e.target.value, error: '' }))}
                      >
                        {manageUsersEditTimezoneOptions.map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                <section className="users-orgs-add-invite-card" aria-labelledby="manage-users-edit-org-heading">
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="manage-users-edit-org-heading" className="users-orgs-add-invite-card-title">
                      Assign organizations
                    </h3>
                    <p className="users-orgs-org-assign-hint">Choose the organizations this user can access.</p>
                  </div>
                  <div className="users-orgs-user-detail-field users-orgs-add-invite-card-body">
                    <label className="users-orgs-org-assign-option users-orgs-org-assign-option--all">
                      <input
                        type="checkbox"
                        checked={Boolean(manageUsersEditModal.allOrganizationsAccess)}
                        disabled={usersActionLoading}
                        onChange={(e) => setManageUsersEditModal((m) => ({
                          ...m,
                          allOrganizationsAccess: e.target.checked,
                          error: '',
                        }))}
                      />
                      <span><strong>All organizations</strong></span>
                    </label>
                    <input
                      id="manage-users-edit-org-search"
                      type="search"
                      className="users-orgs-user-detail-input users-orgs-org-assign-search"
                      value={manageUserOrgSearch}
                      onChange={(e) => setManageUserOrgSearch(e.target.value)}
                      placeholder="Search organizations"
                      autoComplete="off"
                      disabled={usersActionLoading || Boolean(manageUsersEditModal.allOrganizationsAccess)}
                    />
                    <div className="users-orgs-org-assign-list" role="group" aria-label="Assign organizations">
                      {orgsList.length === 0 ? (
                        <p className="users-orgs-org-assign-empty">No organizations available.</p>
                      ) : (() => {
                        const query = manageUserOrgSearch.trim().toLowerCase();
                        const visibleOrgs = orgsList.filter((org) => {
                          if (!manageOrgUuid(org)) return false;
                          if (!query) return true;
                          return String(org?.name || '').toLowerCase().includes(query);
                        });
                        if (visibleOrgs.length === 0) {
                          return <p className="users-orgs-org-assign-empty">No organizations match that search.</p>;
                        }
                        return visibleOrgs.map((org) => {
                            const orgId = manageOrgUuid(org);
                            const checked = (manageUsersEditModal.organizationAccessUuids || []).includes(orgId);
                            return (
                              <label key={orgId} className="users-orgs-org-assign-option">
                                <input
                                  type="checkbox"
                                  checked={checked || Boolean(manageUsersEditModal.allOrganizationsAccess)}
                                  disabled={usersActionLoading || Boolean(manageUsersEditModal.allOrganizationsAccess)}
                                  onChange={(e) => {
                                    const nextChecked = e.target.checked;
                                    setManageUsersEditModal((current) => {
                                      const currentIds = Array.isArray(current.organizationAccessUuids) ? current.organizationAccessUuids : [];
                                      const organizationAccessUuids = nextChecked
                                        ? Array.from(new Set([...currentIds, orgId]))
                                        : currentIds.filter((id) => id !== orgId);
                                      const siteAccessUuids = nextChecked
                                        ? current.siteAccessUuids
                                        : (current.siteAccessUuids || []).filter((siteId) => {
                                          const site = manageUserSites.find((row) => manageSiteUuid(row) === siteId);
                                          return site ? manageSiteOrgUuid(site, orgsList) !== orgId : true;
                                        });
                                      return { ...current, organizationAccessUuids, siteAccessUuids, error: '' };
                                    });
                                  }}
                                />
                                <span><strong>{org.name || orgId}</strong></span>
                              </label>
                            );
                          });
                      })()}
                    </div>
                  </div>
                </section>

                <section
                  className={`users-orgs-add-invite-card${
                    !(manageUsersEditModal.allOrganizationsAccess || (manageUsersEditModal.organizationAccessUuids || []).length)
                      ? ' users-orgs-add-invite-card--disabled'
                      : ''
                  }`}
                  aria-labelledby="manage-users-edit-site-heading"
                >
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="manage-users-edit-site-heading" className="users-orgs-add-invite-card-title">
                      Assign sites
                    </h3>
                    <p className="users-orgs-org-assign-hint">
                      {manageUsersEditModal.allOrganizationsAccess || (manageUsersEditModal.organizationAccessUuids || []).length
                        ? 'Sites stay within the organizations selected above.'
                        : 'Select one or more organizations first to assign sites.'}
                    </p>
                  </div>
                  <div className="users-orgs-user-detail-field users-orgs-add-invite-card-body">
                    <label className="users-orgs-org-assign-option users-orgs-org-assign-option--all">
                      <input
                        type="checkbox"
                        checked={Boolean(manageUsersEditModal.allSitesAccess)}
                        disabled={usersActionLoading}
                        onChange={(e) => setManageUsersEditModal((m) => ({
                          ...m,
                          allSitesAccess: e.target.checked,
                          error: '',
                        }))}
                      />
                      <span><strong>All sites</strong></span>
                    </label>
                    <input
                      id="manage-users-edit-site-search"
                      type="search"
                      className="users-orgs-user-detail-input users-orgs-org-assign-search"
                      value={manageUserSiteSearch}
                      onChange={(e) => setManageUserSiteSearch(e.target.value)}
                      placeholder="Search sites"
                      autoComplete="off"
                      disabled={usersActionLoading || Boolean(manageUsersEditModal.allSitesAccess)}
                    />
                    <div className="users-orgs-org-assign-list" role="group" aria-label="Assign sites">
                      {(() => {
                        const selectedOrgIds = new Set(manageUsersEditModal.organizationAccessUuids || []);
                        const query = manageUserSiteSearch.trim().toLowerCase();
                        const visibleSites = manageUserSites.filter((site) => {
                          const siteId = manageSiteUuid(site);
                          if (!siteId) return false;
                          const siteOrgId = manageSiteOrgUuid(site, orgsList);
                          const selected = (manageUsersEditModal.siteAccessUuids || []).includes(siteId);
                          const inScope = Boolean(manageUsersEditModal.allOrganizationsAccess)
                            || !siteOrgId
                            || selectedOrgIds.has(siteOrgId)
                            || selected;
                          if (!inScope) return false;
                          if (!query) return true;
                          const label = `${site?.site_name || site?.name || ''} ${site?.organization_name || site?.organization || ''}`.toLowerCase();
                          return label.includes(query);
                        });
                        if (visibleSites.length === 0) {
                          return <p className="users-orgs-org-assign-empty">No sites match the selected organizations.</p>;
                        }
                        return visibleSites.map((site) => {
                          const siteId = manageSiteUuid(site);
                          const checked = (manageUsersEditModal.siteAccessUuids || []).includes(siteId);
                          const siteOrgId = manageSiteOrgUuid(site, orgsList);
                          const orgName = orgsList.find((org) => manageOrgUuid(org) === siteOrgId)?.name
                            || site?.organization_name
                            || site?.organization
                            || '';
                          return (
                            <label key={siteId} className="users-orgs-org-assign-option">
                              <input
                                type="checkbox"
                                checked={checked || Boolean(manageUsersEditModal.allSitesAccess)}
                                disabled={usersActionLoading || Boolean(manageUsersEditModal.allSitesAccess)}
                                onChange={(e) => {
                                  const nextChecked = e.target.checked;
                                  setManageUsersEditModal((current) => {
                                    const currentSiteIds = Array.isArray(current.siteAccessUuids) ? current.siteAccessUuids : [];
                                    const siteAccessUuids = nextChecked
                                      ? Array.from(new Set([...currentSiteIds, siteId]))
                                      : currentSiteIds.filter((id) => id !== siteId);
                                    const currentOrgIds = Array.isArray(current.organizationAccessUuids) ? current.organizationAccessUuids : [];
                                    const organizationAccessUuids = nextChecked && siteOrgId && !current.allOrganizationsAccess
                                      ? Array.from(new Set([...currentOrgIds, siteOrgId]))
                                      : currentOrgIds;
                                    return { ...current, siteAccessUuids, organizationAccessUuids, error: '' };
                                  });
                                }}
                              />
                              <span>
                                <strong>{site?.site_name || site?.name || siteId}</strong>
                                {orgName ? <span>{orgName}</span> : null}
                              </span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </section>

                <section className="users-orgs-add-invite-card" aria-labelledby="manage-users-edit-role-heading">
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="manage-users-edit-role-heading" className="users-orgs-add-invite-card-title">
                      Role
                    </h3>
                    <p className="users-orgs-org-assign-hint">Choose the access level for this user.</p>
                  </div>
                  <div className="users-orgs-add-invite-card-body">
                    <div className="users-orgs-user-detail-field">
                      <select
                        id="manage-users-edit-role"
                        className="users-orgs-user-detail-input users-orgs-user-detail-select"
                        aria-labelledby="manage-users-edit-role-heading"
                        value={manageUsersEditModal.role}
                        onChange={(e) => setManageUsersEditModal((m) => ({ ...m, role: e.target.value, error: '' }))}
                      >
                        {Array.from(new Set([...MANAGE_USERS_ROLE_OPTIONS, String(manageUsersEditModal.role || '').trim()].filter(Boolean))).map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                <section className="users-orgs-add-invite-card" aria-labelledby="manage-users-edit-password-heading">
                  <div className="users-orgs-add-invite-card-head">
                    <h3 id="manage-users-edit-password-heading" className="users-orgs-add-invite-card-title">
                      Password
                    </h3>
                    <p className="users-orgs-org-assign-hint">
                      Send a password reset link to {manageUsersEditModal.email || 'this user'}.
                    </p>
                  </div>
                  <div className="users-orgs-add-invite-card-body">
                    <div className="users-orgs-user-detail-field users-orgs-user-detail-reset">
                      <button
                        type="button"
                        className="users-orgs-user-detail-reset-btn"
                        onClick={sendEditUserPasswordReset}
                        disabled={usersActionLoading || !String(manageUsersEditModal.email || '').trim()}
                      >
                        Send reset link
                      </button>
                    </div>
                  </div>
                </section>

                {manageUsersEditModal.resetMessage ? (
                  <div className="profile-settings-message profile-settings-success" role="status">
                    {manageUsersEditModal.resetMessage}
                  </div>
                ) : null}

                {manageUsersEditModal.error ? (
                  <div className="profile-settings-message profile-settings-error" role="alert">
                    {manageUsersEditModal.error}
                  </div>
                ) : null}
                </div>

                <div className="users-orgs-user-detail-actions">
                  <button
                    type="submit"
                    className="users-orgs-user-detail-save-btn"
                    disabled={usersActionLoading}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {mfaRecoveryPasswordOpen ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRecoveryPasswordPrompt();
          }}
        >
          <form
            className="assets-exclude-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-mfa-recovery-password-title"
            onSubmit={handleGenerateRecoveryCodes}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              aria-label="Close"
              onClick={closeRecoveryPasswordPrompt}
              disabled={mfaRecoveryBusy}
            >
              ✕
            </button>
            <h2 id="profile-mfa-recovery-password-title" className="assets-exclude-confirm-title">
              Confirm your password
            </h2>
            <p className="assets-exclude-confirm-desc">
              Enter your current password to regenerate recovery codes. Existing unused codes will stop working.
            </p>
            <div className="users-orgs-delete-confirm-password-block">
              <input
                id="profile-mfa-recovery-password"
                type="password"
                autoComplete="current-password"
                aria-label="Current password"
                className="users-orgs-delete-confirm-password-input"
                value={mfaRecoveryPassword}
                onChange={(e) => setMfaRecoveryPassword(e.target.value)}
                placeholder="Current password"
                disabled={mfaRecoveryBusy}
                autoFocus
              />
              {mfaRecoveryPasswordError ? (
                <p className="users-orgs-delete-confirm-auth-error" role="alert">
                  {mfaRecoveryPasswordError}
                </p>
              ) : null}
            </div>
            <div className="assets-exclude-confirm-actions">
              <button
                type="submit"
                className="assets-exclude-confirm-btn"
                disabled={mfaRecoveryBusy || !mfaRecoveryPassword}
              >
                Regenerate
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {mfaDisablePasswordOpen ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDisablePasswordPrompt();
          }}
        >
          <form
            className="assets-exclude-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-mfa-disable-password-title"
            onSubmit={handleMfaDisable}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              aria-label="Close"
              onClick={closeDisablePasswordPrompt}
              disabled={mfaDisableBusy}
            >
              ✕
            </button>
            <h2 id="profile-mfa-disable-password-title" className="assets-exclude-confirm-title">
              Confirm your password
            </h2>
            <p className="assets-exclude-confirm-desc">
              Enter your current password to turn off multi-factor authentication.
            </p>
            <div className="users-orgs-delete-confirm-password-block">
              <input
                id="profile-mfa-disable-password"
                type="password"
                autoComplete="current-password"
                aria-label="Current password"
                className="users-orgs-delete-confirm-password-input"
                value={mfaDisablePassword}
                onChange={(e) => setMfaDisablePassword(e.target.value)}
                placeholder="Current password"
                disabled={mfaDisableBusy}
                autoFocus
              />
              {mfaDisablePasswordError ? (
                <p className="users-orgs-delete-confirm-auth-error" role="alert">
                  {mfaDisablePasswordError}
                </p>
              ) : null}
            </div>
            <div className="assets-exclude-confirm-actions">
              <button
                type="submit"
                className="assets-exclude-confirm-btn"
                disabled={mfaDisableBusy || !mfaDisablePassword}
              >
                Disable
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {mfaChallengeId && isMfaOtpUiMethod(mfaChallengeMethod)
        ? createPortal(
            <div
              className="profile-settings-mfa-verify-overlay"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !mfaBusy) clearMfaOtpChallenge();
              }}
            >
              <div
                className="profile-settings-mfa-verify-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-mfa-verify-title"
                aria-describedby="profile-mfa-verify-desc"
              >
                <div className="profile-settings-mfa-verify-dialog-head">
                  <h2 id="profile-mfa-verify-title" className="profile-settings-mfa-verify-dialog-title">
                    Verify {mfaMethodDisplayLabel(mfaChallengeMethod)} code
                  </h2>
                  <button
                    type="button"
                    className="profile-settings-mfa-verify-close"
                    aria-label="Close"
                    onClick={clearMfaOtpChallenge}
                    disabled={mfaBusy}
                  >
                    ✕
                  </button>
                </div>
                <p id="profile-mfa-verify-desc" className="profile-settings-mfa-verify-dialog-desc">
                  {normalizeMfaMethod(mfaChallengeMethod) === 'sms'
                    ? 'Enter the 6-digit code we sent to your phone to finish enabling MFA.'
                    : 'Enter the 6-digit code we sent to your email to finish enabling MFA.'}
                </p>
                <div className="profile-settings-mfa-verify-dialog-body">
                  <MfaOtpCodeSlots
                    id="profile-mfa-code"
                    value={mfaCode}
                    onChange={setMfaCode}
                    disabled={mfaBusy}
                    aria-label="Verification code"
                  />
                  {mfaError ? (
                    <div className="profile-settings-mfa-verify-error" role="alert">
                      <p className="profile-settings-mfa-verify-error-text">{formatMfaDialogError(mfaError)}</p>
                    </div>
                  ) : null}
                </div>
                <div className="profile-settings-mfa-verify-dialog-actions">
                  <button
                    type="button"
                    className="profile-settings-submit profile-settings-mfa-inline-send profile-settings-mfa-verify-submit"
                    onClick={handleMfaVerifyCode}
                    disabled={mfaBusy || mfaCode.replace(/\D/g, '').length < MFA_OTP_SLOT_COUNT}
                  >
                    Verify
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {manageUsersDeleteModal ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setManageUsersDeleteModal(null);
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label={
              manageUsersDeleteModal.bulk
                ? 'Confirm delete selected users'
                : 'Confirm delete user'
            }
            aria-describedby="profile-manage-users-delete-desc"
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              aria-label="Close"
              onClick={() => setManageUsersDeleteModal(null)}
              disabled={usersActionLoading}
            >
              ✕
            </button>
            <p id="profile-manage-users-delete-desc" className="assets-exclude-confirm-desc">
              {manageUsersDeleteModal.bulk ? (
                <>
                  You are about to permanently delete{' '}
                  <strong>{manageUsersDeleteModal.users.length}</strong> selected users.
                  <span className="assets-exclude-confirm-warning">
                    This action cannot be undone.
                  </span>
                </>
              ) : (
                <>
                  You are about to permanently delete{' '}
                  <strong>{manageUsersDeleteModal.users[0]?.email}</strong>.
                  <span className="assets-exclude-confirm-warning">
                    This action cannot be undone.
                  </span>
                </>
              )}
            </p>
            {!manageUsersDeleteModal.bulk ? (
              <div className="users-orgs-delete-confirm-password-block">
                <label
                  htmlFor="profile-manage-users-delete-email"
                  className="users-orgs-delete-confirm-password-label"
                >
                  Type the user&apos;s email address to confirm
                </label>
                <input
                  id="profile-manage-users-delete-email"
                  type="email"
                  className="users-orgs-delete-confirm-password-input"
                  autoComplete="off"
                  placeholder={manageUsersDeleteModal.users[0]?.email || 'user@example.com'}
                  value={manageUsersDeleteModal.emailInput}
                  onChange={(e) =>
                    setManageUsersDeleteModal((m) => ({
                      ...m,
                      emailInput: e.target.value,
                      error: '',
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmDeleteUsers();
                    }
                  }}
                />
                {manageUsersDeleteModal.error ? (
                  <p className="users-orgs-delete-confirm-auth-error" role="alert">
                    {manageUsersDeleteModal.error}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteUsers}
                disabled={usersActionLoading}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
